import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { Tooltip, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import { EMAIL_API_URL } from "../../config";

import { placeClickToCall } from "../utils/clickToCall";
import { parsePhones, AdditionalPhones, withCountryCode, dialNumber } from "../utils/phones";
import { canTransferLeads } from "../utils/role";
import { useSelector } from "react-redux";
import { selectLastInboxEvent } from "../../store/auth/authSlice";
import Swal from "sweetalert2";
import moment from "moment";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAdminList,
  useSelectableFollowUpOptions,
  useUpdateEnquiryStatus,
  useAllocateLeads,
} from "../hooks/useManageEnquiry";
import {
  fetchLeadDetails,
  updateLeadContact,
  allocateLeads,
  updateEnquiryStatus as updateEnquiryStatusSilent,
} from "../services/manageEnquiryService";
import {
  useLeadFollowUps,
  useAddFollowUp,
  useUpdateFollowUp,
  useDeleteFollowUp,
} from "../hooks/useFollowUp";
import { useEmailTemplates } from "../hooks/useEmailTemplate";
import { DemoStatusPill, formatDemoWhen } from "../utils/demo";
import { fetchTemplatePreview } from "../services/placeholderService";
import {
  sendLeadEmail,
  sendLeadEmailWithTemplate,
  getLeadConversations,
  getEmailThread,
  markEmailsRead,
} from "../services/emailTemplateService";
import { fetchInit, authHeaders, getSessionToken } from "../utils/session";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const CALL_OUTCOMES = [
  "Connected",
  "No Answer",
  "Voicemail Left",
  "Call Back Later",
  "Wrong Number",
];

// ─── ICONS ───────────────────────────────────────────────────────────────────
const IC = {
  Mail: () => (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  WA: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
  Phone: () => (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .84h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  Note: () => (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Plus: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Send: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  Attach: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  ),
  Audio: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />
    </svg>
  ),
  Video: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  ),
  Image: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  Doc: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  Check: () => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  DblCheck: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="17 1 9 9 5 5" />
      <polyline points="22 6 12 16 8 12" />
    </svg>
  ),
  Trash: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  ),
  Negative: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3z" />
      <path d="M17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" />
    </svg>
  ),
  Calendar: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Clock: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  User: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Building: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="3" width="20" height="18" rx="2" />
      <line x1="8" y1="21" x2="8" y2="3" />
      <line x1="16" y1="21" x2="16" y2="3" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  ),
  MapPin: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  X: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Filter: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  ArrowLeft: () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  Edit: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  PhoneAdd: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .84h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
      <line x1="19" y1="4" x2="19" y2="10" />
      <line x1="16" y1="7" x2="22" y2="7" />
    </svg>
  ),
  ChevronDown: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Layers: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  CC: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8" />
      <path d="M22 8l-6-4" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Eye: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getTypeCfg = (t) =>
  ({
    email: {
      color: "#667eea",
      bg: "#f0f4ff",
      border: "#c7d2fe",
      label: "Email",
      Icon: IC.Mail,
    },
    whatsapp: {
      color: "#16a34a",
      bg: "#f0fdf4",
      border: "#bbf7d0",
      label: "WhatsApp",
      Icon: IC.WA,
    },
    call: {
      color: "#f59e0b",
      bg: "#fffbeb",
      border: "#fde68a",
      label: "Call",
      Icon: IC.Phone,
    },
    note: {
      color: "#8b5cf6",
      bg: "#f5f3ff",
      border: "#ddd6fe",
      label: "Note",
      Icon: IC.Note,
    },
    meeting: {
      color: "#0284c7",
      bg: "#f0f9ff",
      border: "#bae6fd",
      label: "Meeting",
      Icon: IC.Calendar,
    },
    other: {
      color: "#8b5cf6",
      bg: "#f5f3ff",
      border: "#ddd6fe",
      label: "Note",
      Icon: IC.Note,
    },
  })[t] || {
    color: "#6b7280",
    bg: "#f9fafb",
    border: "#e5e7eb",
    label: t,
    Icon: IC.User,
  };

const getMediaIcon = (t) =>
  ({
    audio: <IC.Audio />,
    video: <IC.Video />,
    image: <IC.Image />,
    document: <IC.Doc />,
  })[t] || null;

const parseAttachments = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val.map((item) => {
      if (typeof item === "string") {
        const fileName =
          decodeURIComponent(item.split("/").pop().split("?")[0]) ||
          "Attachment";
        return { name: fileName, url: item };
      }
      if (typeof item === "object" && item !== null) {
        return {
          name:
            item.name ||
            (item.url
              ? decodeURIComponent(item.url.split("/").pop().split("?")[0])
              : "Attachment"),
          url: item.url || "",
          size: item.size || "",
        };
      }
      return { name: "Attachment", url: "" };
    });
  }
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      return parseAttachments(Array.isArray(parsed) ? parsed : [parsed]);
    } catch {
      const fileName =
        decodeURIComponent(val.split("/").pop().split("?")[0]) || "Attachment";
      return [{ name: fileName, url: val }];
    }
  }
  return [];
};

const getFileType = (nameOrUrl) => {
  if (!nameOrUrl) return "other";
  const ext = nameOrUrl.split(".").pop().split("?")[0].toLowerCase();
  if (
    ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico", "tiff"].includes(
      ext,
    )
  )
    return "image";
  if (["mp4", "webm", "mov", "avi", "mkv", "m4v", "ogv"].includes(ext))
    return "video";
  if (["mp3", "wav", "ogg", "aac", "m4a", "flac", "wma"].includes(ext))
    return "audio";
  if (["pdf"].includes(ext)) return "pdf";
  if (["doc", "docx"].includes(ext)) return "word";
  if (["xls", "xlsx", "csv"].includes(ext)) return "excel";
  if (["ppt", "pptx"].includes(ext)) return "ppt";
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return "archive";
  return "other";
};

const FileTypeIcon = ({ type }) => {
  const s = { width: "18px", height: "18px" };
  if (type === "pdf")
    return (
      <span
        style={{
          ...s,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10px",
          fontWeight: 800,
          color: "#dc2626",
          background: "#fef2f2",
          borderRadius: "4px",
          border: "1px solid #fecaca",
        }}
      >
        PDF
      </span>
    );
  if (type === "word")
    return (
      <span
        style={{
          ...s,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "8px",
          fontWeight: 800,
          color: "#2563eb",
          background: "#eff6ff",
          borderRadius: "4px",
          border: "1px solid #bfdbfe",
        }}
      >
        DOC
      </span>
    );
  if (type === "excel")
    return (
      <span
        style={{
          ...s,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "8px",
          fontWeight: 800,
          color: "#16a34a",
          background: "#f0fdf4",
          borderRadius: "4px",
          border: "1px solid #bbf7d0",
        }}
      >
        XLS
      </span>
    );
  if (type === "video") return <IC.Video />;
  if (type === "audio") return <IC.Audio />;
  if (type === "archive") return <IC.Doc />;
  return <IC.Doc />;
};

const forceDownload = async (url, filename) => {
  const name =
    filename ||
    decodeURIComponent(url.split("/").pop().split("?")[0]) ||
    "download";
  try {
    const res = await fetch(url, { mode: "cors" });
    if (res.ok) {
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 150);
      return;
    }
  } catch {}
  try {
    const token = getSessionToken();
    const proxyUrl = `${process.env.REACT_APP_API_BASE_URL}/download-file?url=${encodeURIComponent(url)}`;
    const res = await fetch(proxyUrl, fetchInit());
    if (res.ok) {
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 150);
      return;
    }
  } catch {}
  window.open(url, "_blank");
};

/* A closed lead is not worked: the panel opens read-only until it is reopened.
   `kanban_column` is the backend's own verdict and the one to trust; the label
   and value sets below are only fallbacks for rows/details that arrive without
   a column. Both statuses land in the Negative column server-side. */
const CLOSED_COLUMNS = new Set(["negative"]);
const CLOSED_STATUS_VALUES = new Set(["5", "10"]);   // Not Interested, Negative
const CLOSED_STATUS_LABELS = new Set(["negative", "not interested"]);

/* A Teams invite arrives as a normal outbound email follow-up whose body carries
   the join URL as plain text. These two turn that text into something a rep can
   actually click. */
const URL_RE = /(https?:\/\/\S+)/g;
const isDemoFollowUp = (fu) => String(fu?.template_used || "").startsWith("demo_booking");
const teamsLinkIn = (text) =>
  (String(text || "").match(URL_RE) || []).find((u) => u.includes("teams.microsoft.com")) || null;

/* An email follow-up's body is the sent mail itself — full HTML, logo and all.
   A note or a call log is plain text. Printing HTML as text shows raw tags, and
   running plain text through innerHTML loses its line breaks, so the body picks
   its renderer from the content. */
const looksLikeHtml = (text) => /<\/?[a-z][\s\S]*>/i.test(String(text || ""));

const FollowUpBody = ({ text, style }) =>
  looksLikeHtml(text) ? (
    <div
      style={{ ...style, whiteSpace: "normal" }}
      /* Internally generated (our templates, our invite mails), never pasted by
         a lead — which is what makes rendering it as markup safe here. */
      dangerouslySetInnerHTML={{ __html: text }}
    />
  ) : (
    <LinkifiedText text={text} style={style} />
  );

/** Renders text with its URLs as links, leaving everything else as written. */
const LinkifiedText = ({ text, style }) => {
  const parts = String(text || "").split(URL_RE);
  return (
    <p style={style}>
      {/* Tested with a fresh regex: URL_RE is global, and .test() on a global
          regex carries lastIndex between calls. */}
      {parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: "#2563eb", textDecoration: "underline", wordBreak: "break-all" }}
          >
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </p>
  );
};

const statusColor = (s) => {
  const m = {
    "No Response": "#9ca3af",
    Interested: "#16a34a",
    "Not Interested": "#6b7280",
    "Meeting Scheduled": "#0284c7",
    Converted: "#15803d",
    "Follow Up": "#667eea",
    "Hot Lead": "#ef4444",
    "Mark for Future": "#f59e0b",
    Negative: "#dc2626",
    pending: "#f59e0b",
    completed: "#16a34a",
    cancelled: "#ef4444",
    rescheduled: "#0891b2",
  };
  return m[s] || "#667eea";
};

// ─── CUSTOM FOLLOW-UP STATUS SELECT ──────────────────────────────────────────
const FollowUpStatusSelect = ({
  value,
  onChange,
  followUpOptionsList = [],
  style = {},
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const selected = followUpOptionsList.find((o) => o.option_value === value);
  const color = statusColor(selected?.label || "");

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", ...style }}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        style={{
          width: "100%",
          padding: "9px 36px 9px 12px",
          border: `1.5px solid ${color}`,
          borderRadius: "9px",
          fontSize: "13px",
          fontWeight: 700,
          color: color,
          background: `${color}10`,
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          boxSizing: "border-box",
          transition: "all 0.2s",
          boxShadow: open ? `0 0 0 3px ${color}20` : "none",
          position: "relative",
        }}
      >
        <span style={{ flex: 1 }}>{selected?.label || "Select status"}</span>
        <span
          style={{
            position: "absolute",
            right: "11px",
            top: "50%",
            transform: `translateY(-50%) rotate(${open ? "180deg" : "0deg"})`,
            transition: "transform 0.2s",
            color: color,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
          }}
        >
          <IC.ChevronDown />
        </span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "white",
            border: "1.5px solid #e8ecff",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(102,126,234,0.18)",
            overflow: "hidden",
          }}
        >
          <div
            style={{ maxHeight: "220px", overflowY: "auto", padding: "6px" }}
          >
            {followUpOptionsList.map((opt) => {
              const c = statusColor(opt.label);
              const isActive = opt.option_value === value;
              return (
                <button
                  key={opt.option_value}
                  type="button"
                  onClick={() => {
                    onChange(opt.option_value);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "white" : c,
                    background: isActive ? c : "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    transition: "all 0.15s",
                    marginBottom: "2px",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = `${c}15`;
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {opt.label}
                  {isActive && (
                    <span
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <IC.Check />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const AudioWave = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "2px",
      height: "20px",
    }}
  >
    {[1, 1.6, 0.8, 1.4, 0.6, 1.2, 1, 1.5, 0.7, 1.3].map((h, i) => (
      <div
        key={i}
        style={{
          width: "3px",
          borderRadius: "2px",
          background: "currentColor",
          opacity: 0.7,
          height: `${h * 10}px`,
          animation: `wave ${0.5 + i * 0.1}s ease-in-out infinite alternate`,
        }}
      />
    ))}
    <style>{`@keyframes wave{from{transform:scaleY(0.4)}to{transform:scaleY(1)}}`}</style>
  </div>
);

const nowISO = () => moment().format("YYYY-MM-DD HH:mm:ss");
const createdBy = () => Number(localStorage.getItem("trade_user_id")) || 1;

/** Logged-in user's own name, used to label their own timeline entries. */
const loggedInName = () => {
  try {
    const u = JSON.parse(localStorage.getItem("trade_user_data") || "{}");
    return String(u?.name || "").trim().toLowerCase();
  } catch {
    return "";
  }
};

/**
 * True when a follow-up was logged by the person looking at the screen.
 * The row carries `created_by_name`, not always the id, so fall back to a name
 * match — a colleague's entry must keep showing THEIR name, not "You".
 */
const isMyEntry = (fu) => {
  if (fu?.created_by != null && String(fu.created_by) !== "")
    return String(fu.created_by) === String(createdBy());
  const mine = loggedInName();
  return mine ? String(fu?.created_by_name || "").trim().toLowerCase() === mine : false;
};

const formatDateTime = (utcDate) => {
  if (!utcDate) return "";
  return moment.utc(utcDate).local().format("DD MMM YYYY, hh:mm A");
};

const formatNextFollowUp = (fu) => {
  if (!fu.next_followup_date) return "";
  const raw = fu.next_followup_date;
  const isUTC = raw.includes("T") || raw.includes("Z");
  const datePart = isUTC
    ? moment.utc(raw).local().format("DD MMM YYYY")
    : moment(raw, "YYYY-MM-DD").format("DD MMM YYYY");
  if (fu.next_followup_time) {
    const timePart = moment(fu.next_followup_time, "HH:mm").format("hh:mm A");
    return `${datePart}, ${timePart}`;
  }
  if (isUTC) return moment.utc(raw).local().format("DD MMM YYYY, hh:mm A");
  return datePart;
};

// ─── STAGE UPDATE MODAL ──────────────────────────────────────────────────────
const StageUpdateModal = ({
  currentStatus,
  initialStatus,
  onSave,
  onCancel,
  isLoading,
  followUpOptionsList = [],
}) => {
  const [newStatus, setNewStatus] = useState(initialStatus || currentStatus);
  const [remarks, setRemarks] = useState("");
  const fromColor = statusColor(currentStatus);
  const toColor = statusColor(newStatus);
  const L = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#6b7280",
    display: "block",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  };
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15,23,42,0.5)",
        backdropFilter: "blur(5px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "18px",
          width: "100%",
          maxWidth: "440px",
          boxShadow: "0 28px 64px rgba(0,0,0,0.22)",
          overflow: "hidden",
          animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <style>{`@keyframes modalIn{from{opacity:0;transform:scale(0.92) translateY(14px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>
        <div
          style={{
            background: "linear-gradient(135deg,#667eea,#764ba2)",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "34px",
                height: "34px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <IC.Layers />
            </div>
            <div>
              <div
                style={{ color: "white", fontWeight: 700, fontSize: "15px" }}
              >
                Update Lead Stage
              </div>
            </div>
          </div>
          <button
            onClick={onCancel}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <IC.X />
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "18px",
              padding: "12px 14px",
              background: "#f8f9ff",
              borderRadius: "12px",
              border: "1.5px solid #e8ecff",
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#9ca3af",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "5px",
                }}
              >
                Current
              </div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  background: `${fromColor}18`,
                  color: fromColor,
                  fontSize: "12px",
                  fontWeight: 700,
                  border: `1.5px solid ${fromColor}30`,
                }}
              >
                {currentStatus}
              </span>
            </div>
            <div style={{ color: "#a5b4fc", fontSize: "16px" }}>›</div>
            <div style={{ flex: 1, textAlign: "right" }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#9ca3af",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "5px",
                }}
              >
                New
              </div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  background: `${toColor}18`,
                  color: toColor,
                  fontSize: "12px",
                  fontWeight: 700,
                  border: `1.5px solid ${toColor}30`,
                }}
              >
                {newStatus}
              </span>
            </div>
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>
              Stage <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <select
                style={{
                  width: "100%",
                  padding: "9px 36px 9px 12px",
                  border: `1.5px solid ${toColor}`,
                  borderRadius: "9px",
                  fontSize: "13px",
                  outline: "none",
                  background: "white",
                  cursor: "pointer",
                  boxSizing: "border-box",
                  color: toColor,
                  fontWeight: 700,
                  appearance: "none",
                  WebkitAppearance: "none",
                }}
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                {followUpOptionsList.map((opt) => (
                  <option key={opt.option_value} value={opt.label}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div
                style={{
                  position: "absolute",
                  right: "11px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: toColor,
                  pointerEvents: "none",
                }}
              >
                <IC.ChevronDown />
              </div>
            </div>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <label style={L}>
              Remarks{" "}
              <span
                style={{
                  color: "#9ca3af",
                  fontWeight: 400,
                  fontSize: "10px",
                  textTransform: "none",
                }}
              >
                (optional)
              </span>
            </label>
            <textarea
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1.5px solid #e2e8f0",
                borderRadius: "9px",
                fontSize: "13px",
                outline: "none",
                resize: "vertical",
                minHeight: "76px",
                background: "white",
                fontFamily: "inherit",
                boxSizing: "border-box",
                lineHeight: 1.6,
              }}
              placeholder="Reason for this change..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={onCancel}
              style={{
                flex: 1,
                padding: "10px",
                background: "#f1f5f9",
                color: "#6b7280",
                border: "1.5px solid #e2e8f0",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              disabled={isLoading}
              onClick={() =>
                onSave({
                  follow_up_status: newStatus,
                  ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
                })
              }
              style={{
                flex: 2,
                padding: "10px",
                background: `linear-gradient(135deg,${toColor},${toColor}cc)`,
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "7px",
                boxShadow: `0 4px 14px ${toColor}40`,
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              <IC.Check />
              {isLoading ? "Updating..." : "Update Stage"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── FILE CHIP COMPONENT ─────────────────────────────────────────────────────
const FileChip = ({ file, onRemove }) => {
  const name = file instanceof File ? file.name : file.name || "Attachment";
  const rawSize =
    file instanceof File ? file.size : file.size ? Number(file.size) : 0;
  const sizeLabel = rawSize
    ? rawSize > 1024 * 1024
      ? `${(rawSize / (1024 * 1024)).toFixed(1)} MB`
      : `${(rawSize / 1024).toFixed(0)} KB`
    : "";
  const fileUrl = file instanceof File ? URL.createObjectURL(file) : file.url;
  const isImage =
    file instanceof File
      ? file.type?.startsWith("image/")
      : /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(name);
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 10px",
        background: "#f0f4ff",
        border: "1px solid #c7d2fe",
        borderRadius: "8px",
        fontSize: "11px",
        color: "#667eea",
        fontWeight: 600,
        maxWidth: "260px",
      }}
    >
      {isImage && fileUrl ? (
        <img
          src={fileUrl}
          alt={name}
          style={{
            width: "28px",
            height: "28px",
            borderRadius: "5px",
            objectFit: "cover",
            flexShrink: 0,
            border: "1px solid #c7d2fe",
          }}
        />
      ) : (
        <IC.Doc />
      )}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "1px",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "11px",
            fontWeight: 600,
            color: "#374151",
          }}
        >
          {name}
        </span>
        {sizeLabel && (
          <span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 400 }}>
            {sizeLabel}
          </span>
        )}
      </div>
      {file.url && (
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer"
          style={{
            color: "#667eea",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            textDecoration: "none",
            fontSize: "13px",
            fontWeight: 700,
          }}
          title="Download"
        >
          ↓
        </a>
      )}
      {onRemove && (
        <button
          onClick={onRemove}
          style={{
            background: "none",
            border: "none",
            color: "#dc2626",
            cursor: "pointer",
            padding: "2px",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            borderRadius: "4px",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#fef2f2")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
        >
          <IC.X />
        </button>
      )}
    </div>
  );
};

// ─── EDIT FOLLOW-UP MODAL ────────────────────────────────────────────────────
const EditFollowUpModal = ({
  fu,
  onSave,
  onCancel,
  isLoading,
  followUpOptionsList = [],
}) => {
  const fileInputRef = useRef();
  const supportsAttachments =
    fu.followup_type === "email" || fu.followup_type === "whatsapp";
  const existingAttachments = parseAttachments(fu.attachments);

  const getInitialDate = () => {
    if (!fu.next_followup_date) return "";
    const raw = fu.next_followup_date;
    if (raw.includes("T") || raw.includes("Z"))
      return moment.utc(raw).local().format("YYYY-MM-DD");
    return raw.slice(0, 10);
  };

  const getInitialTime = () => {
    if (fu.next_followup_time) return fu.next_followup_time;
    if (!fu.next_followup_date) return "";
    return moment.utc(fu.next_followup_date).local().format("HH:mm");
  };

  const [data, setData] = useState({
    enquiry_status: String(fu?.enquiry_status ?? "0"),
    remarks: fu?.remarks || "",
    next_followup_date: getInitialDate(),
    next_followup_time: getInitialTime(),
  });

  useEffect(() => {
    if (followUpOptionsList.length === 0) return;
    setData((prev) => {
      if (
        followUpOptionsList.find(
          (o) => o.option_value === String(prev.enquiry_status),
        )
      ) {
        return prev;
      }
      const match = followUpOptionsList.find(
        (o) => o.label === prev.enquiry_status,
      );
      return { ...prev, enquiry_status: match ? match.option_value : "0" };
    });
  }, [followUpOptionsList]);

  const [keptAttachments, setKeptAttachments] = useState(existingAttachments);
  const [newFiles, setNewFiles] = useState([]);

  const removeKeptAttachment = (idx) =>
    setKeptAttachments((p) => p.filter((_, i) => i !== idx));
  const addNewFiles = (e) => {
    if (!e.target.files?.length) return;
    setNewFiles((p) => [...p, ...Array.from(e.target.files)]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const removeNewFile = (idx) =>
    setNewFiles((p) => p.filter((_, i) => i !== idx));

  const L = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#6b7280",
    display: "block",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  };
  const inp = {
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #e2e8f0",
    borderRadius: "9px",
    fontSize: "13px",
    outline: "none",
    background: "white",
    boxSizing: "border-box",
  };
  const cfg = getTypeCfg(fu.followup_type);

  const handleSave = () => {
    const payload = {
      id: fu.id,
      lead_id: fu.lead_id,
      enquiry_status: data.enquiry_status,
      remarks: data.remarks,
      ...(data.next_followup_date
        ? { next_followup_date: data.next_followup_date }
        : {}),
      ...(data.next_followup_time
        ? { next_followup_time: data.next_followup_time }
        : {}),
    };
    if (supportsAttachments) {
      if (keptAttachments.length > 0)
        payload.existing_attachments = JSON.stringify(keptAttachments);
      if (newFiles.length > 0) payload.attachments = newFiles;
    }
    onSave(payload);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "18px",
          width: "100%",
          maxWidth: "440px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
          overflow: "auto",
          maxHeight: "90vh",
          animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <div
          style={{
            background:
              cfg.color === "#667eea"
                ? "linear-gradient(135deg,#667eea,#764ba2)"
                : `linear-gradient(135deg,${cfg.color},${cfg.color}cc)`,
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "9px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <cfg.Icon />
            </div>
            <div>
              <span
                style={{ color: "white", fontWeight: 700, fontSize: "15px" }}
              >
                Edit {cfg.label} Follow-Up
              </span>
              <div
                style={{
                  color: "rgba(255,255,255,.7)",
                  fontSize: "11px",
                  marginTop: "2px",
                }}
              >
                {formatDateTime(fu.followup_date)}
              </div>
            </div>
          </div>
          <button
            onClick={onCancel}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <IC.X />
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>Follow-up Status</label>
            <FollowUpStatusSelect
              value={data.enquiry_status}
              followUpOptionsList={followUpOptionsList}
              onChange={(val) =>
                setData((p) => ({ ...p, enquiry_status: val }))
              }
            />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>
              <IC.Note /> Remarks
            </label>
            <textarea
              style={{
                ...inp,
                resize: "vertical",
                minHeight: "80px",
                fontFamily: "inherit",
                lineHeight: 1.6,
              }}
              placeholder="Update remarks..."
              value={data.remarks}
              onChange={(e) =>
                setData((p) => ({ ...p, remarks: e.target.value }))
              }
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              marginBottom: "14px",
            }}
          >
            <div>
              <label style={L}>
                <IC.Calendar /> Next Follow-up
              </label>
              <input
                type="date"
                style={inp}
                value={data.next_followup_date}
                onChange={(e) =>
                  setData((p) => ({ ...p, next_followup_date: e.target.value }))
                }
              />
            </div>
            <div>
              <label style={L}>
                <IC.Clock /> Time
              </label>
              <input
                type="time"
                style={inp}
                value={data.next_followup_time}
                onChange={(e) =>
                  setData((p) => ({ ...p, next_followup_time: e.target.value }))
                }
                onClick={(e) => e.target.showPicker?.()}
              />
            </div>
          </div>
          {supportsAttachments && (
            <div style={{ marginBottom: "20px" }}>
              <label style={L}>
                <IC.Attach /> Attachments
              </label>
              {keptAttachments.length > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#9ca3af",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      marginBottom: "5px",
                    }}
                  >
                    Existing Files
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                  >
                    {keptAttachments.map((att, i) => (
                      <FileChip
                        key={`e-${i}`}
                        file={{
                          name:
                            att.name ||
                            att.url?.split("/").pop() ||
                            "Attachment",
                          url: att.url,
                          size: att.size ? Number(att.size) : 0,
                        }}
                        onRemove={() => removeKeptAttachment(i)}
                      />
                    ))}
                  </div>
                </div>
              )}
              {newFiles.length > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#16a34a",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      marginBottom: "5px",
                    }}
                  >
                    New Files
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                  >
                    {newFiles.map((f, i) => (
                      <FileChip
                        key={`n-${i}`}
                        file={f}
                        onRemove={() => removeNewFile(i)}
                      />
                    ))}
                  </div>
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "7px 14px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: `1.5px solid ${cfg.border}`,
                  background: cfg.bg,
                  color: cfg.color,
                  marginTop: "4px",
                }}
              >
                <IC.Attach /> Add Files
              </button>
              <input
                type="file"
                multiple
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={addNewFiles}
              />
            </div>
          )}
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={onCancel}
              style={{
                flex: 1,
                padding: "10px",
                background: "#f1f5f9",
                color: "#6b7280",
                border: "1.5px solid #e2e8f0",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              disabled={isLoading}
              onClick={handleSave}
              style={{
                flex: 2,
                padding: "10px",
                background: `linear-gradient(135deg,${cfg.color},${cfg.color}cc)`,
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "7px",
                boxShadow: `0 4px 14px ${cfg.color}40`,
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              <IC.Check />
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── CONTACT EDIT MODAL ──────────────────────────────────────────────────────
const ContactEditModal = ({ contact, onSave, onCancel, isLoading }) => {
  // additional_phone can hold any number of alternates — the form keeps them as
  // an array and always hands back an array, whatever the API sent in.
  const [data, setData] = useState({
    ...contact,
    additional_phone: parsePhones(contact?.additional_phone),
  });
  const altPhones = Array.isArray(data.additional_phone) ? data.additional_phone : [];
  const setAlt = (next) => setData((p) => ({ ...p, additional_phone: next }));
  const altError = (val, idx) => {
    if (!val) return "";
    if (val.length !== 10) return `Phone must be 10 digits (${val.length}/10)`;
    if (val === data.phone) return "Alt number cannot be same as primary phone";
    if (altPhones.findIndex((v) => v === val) !== idx) return "This number is already added";
    return "";
  };
  const altErrors = altPhones.map(altError);
  const hasAltError = altErrors.some(Boolean);
  const L = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#6b7280",
    display: "block",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  };
  const inp = {
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #e2e8f0",
    borderRadius: "9px",
    fontSize: "13px",
    outline: "none",
    background: "white",
    boxSizing: "border-box",
  };
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "18px",
          width: "100%",
          maxWidth: "420px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
          overflow: "hidden",
          animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg,#667eea,#764ba2)",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "9px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <IC.User />
            </div>
            <span style={{ color: "white", fontWeight: 700, fontSize: "15px" }}>
              Edit Contact
            </span>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <IC.X />
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>Full Name</label>
            <input
              style={inp}
              value={data.name}
              onChange={(e) => setData((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>Email</label>
            <input
              style={inp}
              type="email"
              value={data.email}
              onChange={(e) =>
                setData((p) => ({ ...p, email: e.target.value }))
              }
            />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>Phone</label>
            <input
              style={{
                ...inp,
                ...(data.phone &&
                data.phone.length > 0 &&
                data.phone.length < 10
                  ? { borderColor: "#f59e0b" }
                  : {}),
              }}
              value={data.phone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                setData((p) => ({ ...p, phone: val }));
              }}
              placeholder="10-digit phone number"
              maxLength={10}
            />
            {data.phone && data.phone.length > 0 && data.phone.length < 10 && (
              <div
                style={{ fontSize: "10px", color: "#f59e0b", marginTop: "4px" }}
              >
                Phone must be 10 digits ({data.phone.length}/10)
              </div>
            )}
          </div>
          <div style={{ marginBottom: "22px" }}>
            <label
              style={{
                ...L,
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <IC.PhoneAdd /> Alt Numbers{" "}
              <span
                style={{
                  color: "#9ca3af",
                  fontWeight: 400,
                  fontSize: "10px",
                  textTransform: "none",
                }}
              >
                (optional — add as many as needed)
              </span>
            </label>

            {altPhones.map((val, idx) => (
              <div key={idx} style={{ marginBottom: "8px" }}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <input
                    style={{
                      ...inp,
                      ...(altErrors[idx]
                        ? { borderColor: altErrors[idx].startsWith("Phone must") ? "#f59e0b" : "#ef4444" }
                        : {}),
                    }}
                    value={val}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setAlt(altPhones.map((p, i) => (i === idx ? v : p)));
                    }}
                    placeholder="10-digit alt number"
                    maxLength={10}
                  />
                  <button
                    onClick={() => setAlt(altPhones.filter((_, i) => i !== idx))}
                    title="Remove this number"
                    style={{
                      width: "34px", height: "34px", flexShrink: 0,
                      borderRadius: "9px", border: "1.5px solid #fecaca",
                      background: "#fef2f2", color: "#dc2626",
                      fontSize: "15px", lineHeight: 1, cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
                {altErrors[idx] && (
                  <div style={{ fontSize: "10px", color: altErrors[idx].startsWith("Phone must") ? "#f59e0b" : "#ef4444", marginTop: "4px" }}>
                    {altErrors[idx]}
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={() => setAlt([...altPhones, ""])}
              disabled={altPhones.some((p) => !p)}
              style={{
                width: "100%", padding: "8px",
                border: "1.5px dashed #c7d2fe", borderRadius: "9px",
                background: "#f8f9ff", color: "#667eea",
                fontSize: "12px", fontWeight: 600,
                cursor: altPhones.some((p) => !p) ? "not-allowed" : "pointer",
                opacity: altPhones.some((p) => !p) ? 0.6 : 1,
              }}
            >
              + Add {altPhones.length ? "another" : "an"} alt number
            </button>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={onCancel}
              disabled={isLoading}
              style={{
                flex: 1,
                padding: "10px",
                background: "#f1f5f9",
                color: "#6b7280",
                border: "1.5px solid #e2e8f0",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (data.phone && data.phone.length !== 10) return;
                if (hasAltError) return;
                // Blank rows the user added but never filled are just dropped.
                onSave({ ...data, additional_phone: parsePhones(altPhones) });
              }}
              disabled={
                isLoading ||
                (data.phone && data.phone.length !== 10) ||
                hasAltError
              }
              style={{
                flex: 2,
                padding: "10px",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "7px",
                boxShadow: "0 4px 14px rgba(102,126,234,0.35)",
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              <IC.Check />
              {isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── EMAIL PREVIEW MODAL ─────────────────────────────────────────────────────
const EmailPreviewModal = ({ subject, body, onClose }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(15,23,42,0.5)",
      backdropFilter: "blur(5px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
    }}
  >
    <div
      style={{
        background: "white",
        borderRadius: "18px",
        width: "100%",
        maxWidth: "700px",
        boxShadow: "0 28px 64px rgba(0,0,0,0.22)",
        overflow: "hidden",
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg,#667eea,#764ba2)",
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              background: "rgba(255,255,255,0.2)",
              borderRadius: "9px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <IC.Eye />
          </div>
          <div>
            <div style={{ color: "white", fontWeight: 700, fontSize: "14px" }}>
              Template Preview
            </div>
            <div
              style={{
                color: "rgba(255,255,255,.7)",
                fontSize: "11px",
                marginTop: "1px",
              }}
            >
              {subject}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            width: "28px",
            height: "28px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            border: "none",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <IC.X />
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0" }}>
        <div
          dangerouslySetInnerHTML={{ __html: body }}
          style={{ minHeight: "200px" }}
        />
      </div>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const EnquiryFollowUp = ({
  enquiry,
  onClose,
  onDeleteLead,
  onNegativeClosed,
}) => {
  const lead = enquiry || {
    id: 0,
    name: "Unknown",
    service: "Enquiry",
    email: "",
    phone: "",
    status: "Neutral",
    assignedTo: "Unassigned",
  };
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /* The lead row's own status, as we last knew it. Needed because a follow-up
     logged with the DEFAULT status ("0" Follow Up) used to be skipped — so a
     brand-new lead stayed NULL and the Kanban kept it in "New" even after a mail
     went out. Now the default is written when the lead has no status yet, while
     an existing status (Hot Lead, Interested…) is never downgraded to it. */
  const knownStatusRef = useRef(String(lead.status ?? "").trim());
  const shouldWriteStatus = (value) =>
    Boolean(value) && (value !== "0" || !knownStatusRef.current);

  /* Returns the helper's promise: it resolves false when the dialer refused the
     call (number not registered, missing, network error), and the caller uses
     that to keep the call-log form closed — a log for a call that never happened
     is worse than no log. */
  const handleClickToCall = (clientNo, clientName) =>
    placeClickToCall(clientNo, clientName);

  const [activePanel, setActivePanel] = useState(null);

  const _internalUserId = Number(localStorage.getItem("trade_user_id")) || 0;
  const _authHdr = useCallback(() => ({ ...authHeaders() }), []);

  const [activeFilter, setActiveFilter] = useState("All");
  const [followUpStatus, setFollowUpStatus] = useState(
    lead.lead_stage || lead.follow_up_status || "Follow Up",
  );
  /* Mark Positive updates the lead server-side, but the `lead` prop this panel
     was handed still carries status 10 until the host refetches. Without this
     the actions would stay locked right after reopening the lead. */
  const [reopened, setReopened] = useState(false);
  /* The board column is the backend's own verdict on where a lead sits, so it
     is what decides "closed" here too — "Not Interested" lands in Negative just
     like an explicit Negative does. Seeded from the row, refreshed from the
     detail call below. */
  const [leadColumn, setLeadColumn] = useState(lead.kanban_column || "");
  // Demo fields as the detail call last reported them; the row is the seed.
  const [demoInfo, setDemoInfo] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [editingFU, setEditingFU] = useState(null);
  const [showContactEdit, setShowContactEdit] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contact, setContact] = useState({
    name: lead.name,
    email: lead.email,
    phone: lead.phone,
    additional_phone: lead.additional_phone || "",
  });

  const [emailTo, setEmailTo] = useState(lead.email || "");
  const [emailCcList, setEmailCcList] = useState([]);
  const [emailCcInput, setEmailCcInput] = useState("");
  const [emailCcError, setEmailCcError] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailBodyMode, setEmailBodyMode] = useState("compose"); // "compose" | "html" | "preview"
  const emailEditorRef = useRef(null);
  const emailExecCmd = useCallback((cmd, val) => {
    emailEditorRef.current?.focus();
    document.execCommand(cmd, false, val);
  }, []);
  useEffect(() => {
    if (emailBodyMode === "compose" && emailEditorRef.current) {
      emailEditorRef.current.innerHTML = emailBody;
    }
  }, [emailBodyMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [emailFiles, setEmailFiles] = useState([]);
  const [templateAttachments, setTemplateAttachments] = useState([]);
  const [emailNextDate, setEmailNextDate] = useState("");
  const [emailNextTime, setEmailNextTime] = useState("");
  const [emailRemarks, setEmailRemarks] = useState("");
  const [emailFuStatus, setEmailFuStatus] = useState("0");
  const [showPreview, setShowPreview] = useState(false);

  const [waBody, setWaBody] = useState("");
  const [waFiles, setWaFiles] = useState([]);
  const [waNextDate, setWaNextDate] = useState("");
  const [waNextTime, setWaNextTime] = useState("");
  const [waRemarks, setWaRemarks] = useState("");
  const [waFuStatus, setWaFuStatus] = useState("pending");

  const [callOutcome, setCallOutcome] = useState("Connected");
  const [callNote, setCallNote] = useState("");
  const [callDuration, setCallDuration] = useState("");
  const [callNextDate, setCallNextDate] = useState("");
  const [callNextTime, setCallNextTime] = useState("");
  const [callFuStatus, setCallFuStatus] = useState("0");
  const [noteText, setNoteText] = useState("");
  const [noteNextDate, setNoteNextDate] = useState("");
  const [noteNextTime, setNoteNextTime] = useState("");
  const [showEmailCc, setShowEmailCc] = useState(false);
  const [showEmailBcc, setShowEmailBcc] = useState(false);
  const [emailBccList, setEmailBccList] = useState([]);
  const [emailBccInput, setEmailBccInput] = useState("");
  const [emailBccError, setEmailBccError] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [activeConvId, setActiveConvId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  // All conversations with their messages preloaded: [{ conversation_id, subject, lastAt, messages }]
  const [inboxGroups, setInboxGroups] = useState([]);
  const setThreadMessagesSorted = useCallback((msgs) => {
    const arr = Array.isArray(msgs) ? [...msgs] : [];
    arr.sort((a, b) => {
      const ta = a.sent_at || a.created_at ? new Date(a.sent_at || a.created_at).getTime() : 0;
      const tb = b.sent_at || b.created_at ? new Date(b.sent_at || b.created_at).getTime() : 0;
      return tb - ta;
    });
    setThreadMessages(arr);
  }, []);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyConvId, setReplyConvId] = useState(null);

  const emailFileRef = useRef();
  const waFileRef = useRef();
  const composeScrollRef = useRef();

  // ── Hooks ──────────────────────────────────────────────────────────────────
  const { data: adminList = [] } = useAdminList();
  const filterForApi = activeFilter === "Note" ? "Other" : (activeFilter === "Conversations" || activeFilter === "All") ? "All" : activeFilter;
  const { data: followUps = [], isLoading: fuLoading } = useLeadFollowUps(
    lead.id,
    filterForApi,
  );
  const { data: allFollowUps = [], isLoading: allFuLoading } = useLeadFollowUps(lead.id, "All");

  // Merge email thread messages into the "All" timeline — must be after followUps
  // is declared. Both directions are included: mails sent from the Lead Inbox
  // screen never create a follow-up record, so filtering to inbound only made
  // those outbound mails invisible here.
  //
  // Outbound mails sent from THIS screen do create an email follow-up, so those
  // are skipped to avoid the same mail appearing twice (matched on subject +
  // a send time within 3 minutes of the follow-up).
  // Computed regardless of the active filter so the "All" badge count stays stable
  // while viewing Call / Note; timelineItems only merges it in on the All tab.
  const emailThreadItems = React.useMemo(() => {
    // Every conversation's messages, not just the active thread's
    const sourceMessages = inboxGroups.length
      ? inboxGroups.flatMap((g) => g.messages)
      : threadMessages;

    return sourceMessages.map((m) => ({
      ...m,
      _isInboundEmail: true, // flags "render as a thread email", either direction
      _sortDate: m.created_at || m.sent_at,
    }));
  }, [inboxGroups, threadMessages]);

  /**
   * Emails are never rendered from the follow-up log in the "All" timeline —
   * every mail already exists as a thread message (inbound and outbound), which
   * renders the HTML properly and links to the conversation.
   *
   * The follow-up row is only the activity record; showing it too produced a
   * duplicate. Body/time matching was tried first, but the two records carry
   * timestamps in different zones and the follow-up loads before the thread, so
   * the duplicate flashed on screen before disappearing. Excluding email-type
   * follow-ups outright is both simpler and flicker-free.
   *
   * Call / note / visit follow-ups are unaffected — those have no thread copy.
   */
  const isEmailFollowUp = (fu) =>
    String(fu?.followup_type || "").trim().toLowerCase() === "email";

  /* Email follow-ups are normally hidden in "All" because the same mails come
     back richer from the conversation thread. A demo invite is the exception:
     the backend sends it from the rep's mailbox and logs it here, so it is in no
     thread — dropping it made it vanish from the page entirely. */
  const isThreadedEmail = (fu) => isEmailFollowUp(fu) && !isDemoFollowUp(fu);

  const timelineItems = React.useMemo(() => {
    if (activeFilter !== "All") {
      return [...followUps].sort((a, b) => {
        const ta = a.followup_date || a.created_at ? new Date(a.followup_date || a.created_at).getTime() : 0;
        const tb = b.followup_date || b.created_at ? new Date(b.followup_date || b.created_at).getTime() : 0;
        return tb - ta;
      });
    }
    const fuWithDate = followUps
      // Threaded emails come from the conversation; the demo invite does not.
      .filter((fu) => !isThreadedEmail(fu))
      .map((fu) => ({ ...fu, _sortDate: fu.followup_date || fu.created_at }));
    return [...fuWithDate, ...emailThreadItems].sort((a, b) => {
      const ta = a._sortDate ? new Date(a._sortDate).getTime() : 0;
      const tb = b._sortDate ? new Date(b._sortDate).getTime() : 0;
      return tb - ta;
    });
  }, [activeFilter, followUps, emailThreadItems]);
  const addFollowUp = useAddFollowUp();
  const updateFollowUp = useUpdateFollowUp();
  const deleteFollowUp = useDeleteFollowUp();

  // Force-refetch follow-ups on mount so newly added follow-ups from
  // manageenquiry.jsx (call/email modals) always appear immediately
  useEffect(() => {
    queryClient.refetchQueries({
      predicate: (q) =>
        q.queryKey[0] === "lead-followups" &&
        String(q.queryKey[1]) === String(lead.id),
    });
  }, [lead.id]);

  // Auto-assign lead to logged-in user if not yet assigned
  /* Ownership gate.
     A lead assigned to a team member must be taken over BEFORE following up:
     the mail goes out from the logged-in user, and the template placeholders are
     resolved from the lead's assignee on the server — so following up on someone
     else's lead would send their name and email in your signature. Taking over
     first makes the lead yours and everything resolves correctly. */
  const [tookOver, setTookOver] = useState(false);
  const [takingOver, setTakingOver] = useState(false);
  /* Hosts map the owner as `assignToId` (LeadCommunication, AssignedLeads, …)
     while the raw API row calls it `assign_to` — accept both, otherwise an
     assigned lead reads as unassigned and both the gate and the auto-assign
     below misbehave. */
  const rawOwner = lead.assign_to ?? lead.assignToId ?? null;
  const leadOwnerId = rawOwner == null || rawOwner === "" ? null : String(rawOwner);
  const isMyLead =
    tookOver || leadOwnerId === null || leadOwnerId === String(createdBy());
  const mayTakeOver = canTransferLeads();

  /* A lead that is already Negative cannot be "closed" again — the same slot
     offers the way back instead: reopen it as a positive lead so it leaves the
     Negative list and returns to the pipeline. */
  /* `reopened` mirrors `tookOver`: the server is already updated, but the `lead`
     prop still carries status 10 until the host refetches, so the panel trusts
     its own confirmed action to unlock immediately. */
  const isNegativeLead =
    !reopened &&
    (CLOSED_COLUMNS.has(String(leadColumn || "").trim().toLowerCase()) ||
      CLOSED_STATUS_LABELS.has(String(followUpStatus || "").trim().toLowerCase()) ||
      CLOSED_STATUS_VALUES.has(String(lead.status ?? "").trim()) ||
      CLOSED_STATUS_LABELS.has(String(lead.status ?? "").trim().toLowerCase()));

  /* Everything that talks to the lead (dial, mail, edit contact) is dead until
     the lead is ours — otherwise the call/mail goes out under a teammate's name.
     A negative lead is closed, so the same actions stay shut there too: the page
     is still reachable to read the history, but working the lead again means
     reopening it with Mark Positive first. */
  const lockedStyle =
    isMyLead && !isNegativeLead ? undefined : { opacity: 0.4, pointerEvents: "none" };
  const canAct = isMyLead && !isNegativeLead;

  /**
   * What the proposal / quotation form should open with.
   *
   * Only the fields the lead actually has — a blank sent as "" would overwrite
   * the form's own default with nothing. The attorney number, when there is one,
   * makes the create screen look up its trademark count on arrival, so the rep
   * does not have to know it.
   */
  const quotePayload = () => {
    const attorney = String(lead.attorney_number ?? "").trim();
    return {
      // Keeps the quote tied to the lead it came from, all the way to payment.
      ...(lead.id ? { lead_id: lead.id } : {}),
      // The company is who the document is addressed to; the person is the contact.
      ...(lead.company || lead.name ? { client_name: lead.company || lead.name } : {}),
      ...(lead.name ? { contact_name: lead.name } : {}),
      ...(lead.email ? { email: lead.email } : {}),
      ...(contact?.phone || lead.phone ? { phone: contact?.phone || lead.phone } : {}),
      ...(lead.country_code ? { country_code: lead.country_code } : {}),
      ...(lead.address ? { address: lead.address } : {}),
      ...(attorney ? { attorney_numbers: [attorney] } : {}),
    };
  };

  /* The row carries the demo fields on most screens; the detail call overrides
     them once it lands, so a booking made elsewhere still shows up here. */
  const demoLead = { ...lead, ...(demoInfo || {}) };

  /* Marking negative is the panel's own job: hosts used to have to pass an
     onNegativeClosed handler, and the Inbox never did — so the button silently
     threw there. The callback is now optional, purely for the host to close and
     refresh. */
  const handleMarkNegative = () => {
    Swal.fire({
      title: "Mark as Negative?",
      html: `<b>${lead.name || "This lead"}</b> will move to the Negative list.`,
      input: "textarea",
      inputLabel: "Remark",
      inputPlaceholder: "Why is this lead negative?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Mark Negative",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      const remark = r.value || "";
      updateEnquiryStatus.mutate(
        { id: lead.id, status: 10, message: remark },
        {
          onSuccess: () => {
            // Closing it again drops the reopen override, so the lock returns
            // even when the host keeps this panel open.
            setReopened(false);
            setFollowUpStatus(getLabelByValue("10") || "Negative");
            Swal.fire({
              icon: "success",
              title: "Marked Negative",
              text: `${lead.name || "The lead"} moved to the Negative list.`,
              confirmButtonColor: "#667eea",
            });
            if (typeof onNegativeClosed === "function") onNegativeClosed(remark);
            else if (typeof onClose === "function") onClose();
          },
          onError: (err) =>
            Swal.fire("Error", err?.message || "Could not update this lead.", "error"),
        },
      );
    });
  };

  const handleMarkPositive = () => {
    Swal.fire({
      title: "Mark as Positive?",
      html: `<b>${lead.name || "This lead"}</b> will move out of the Negative list and back into the pipeline.`,
      input: "textarea",
      inputLabel: "Remark",
      inputPlaceholder: "What changed?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Mark Positive",
      confirmButtonColor: "#16a34a",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      const remark = r.value || "";
      updateEnquiryStatus.mutate(
        { id: lead.id, status: 1, message: remark },
        {
          onSuccess: () => {
            setFollowUpStatus(getLabelByValue("1"));
            setReopened(true);
            Swal.fire({
              icon: "success",
              title: "Marked Positive",
              text: `${lead.name || "The lead"} is back in the pipeline.`,
              confirmButtonColor: "#667eea",
            });
          },
          onError: (err) =>
            Swal.fire("Error", err?.message || "Could not update this lead.", "error"),
        },
      );
    });
  };

  const handleTakeOver = () => {
    Swal.fire({
      title: "Take over this lead?",
      html: `<b>${lead.name || "This lead"}</b> is assigned to <b>${lead.assignedToName || "another user"}</b>.<br/><br/>Taking over assigns it to you, so your follow-ups and your signature go out on it.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, take over",
      confirmButtonColor: "#667eea",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      const userId = parseInt(localStorage.getItem("trade_user_id"), 10);
      if (!userId) return;
      setTakingOver(true);
      allocateLeads({ internal_user_id: userId, lead_ids: [lead.id] })
        .then(() => {
          setTookOver(true);
          queryClient.invalidateQueries({ queryKey: ["enquiryList"], refetchType: "all" });
          queryClient.invalidateQueries({ queryKey: ["inbox-leads"], refetchType: "all" });
          queryClient.invalidateQueries({ queryKey: ["lead-details", lead.id] });
          toast.success("Lead is yours now — you can follow up.");
        })
        .catch((err) => Swal.fire("Error", err?.message || "Could not take over this lead.", "error"))
        .finally(() => setTakingOver(false));
    });
  };

  const autoAssignToSelf = useCallback(() => {
    // Only an genuinely unassigned lead is claimed automatically — never one that
    // already belongs to a teammate (that needs an explicit take-over).
    if (leadOwnerId !== null) return;
    if (isLeadAssigned.current) return;
    const userId = parseInt(localStorage.getItem("trade_user_id"), 10);
    if (!userId) return;
    allocateLeads({ internal_user_id: userId, lead_ids: [lead.id] }).catch(
      () => {},
    );
  }, [leadOwnerId, lead.id]);

  // ── Follow-up options from API ─────────────────────────────────────────────
  // Selectable list: "Converted" is set by creating a subscription, not by hand.
  const { data: followUpOptionsList = [], isLoading: optionsLoading } =
    useSelectableFollowUpOptions();

  // ── useUpdateEnquiryStatus for stage dropdown ──────────────────────────────
  const updateEnquiryStatus = useUpdateEnquiryStatus();

  // ── Auto-assign: allocate lead to logged-in admin before first follow-up ──
  const allocateLeadsMutation = useAllocateLeads();
  const isLeadAssigned = useRef((lead.assign_to ?? lead.assignToId ?? null) != null);

  // Email template hooks
  // Lead screens only offer lead templates (context_type === "lead")
  const { data: emailTemplates = [], isLoading: templatesLoading } =
    useEmailTemplates("lead");

  // Load every conversation AND its messages up front, so the Inbox tab can show
  // the whole email history inline instead of a click-through thread list.
  const loadAllConversations = useCallback(async () => {
    if (!lead.id) return;
    setConvLoading(true);
    try {
      const convs = (await getLeadConversations(lead.id)) || [];
      setConversations(convs);

      const groups = await Promise.all(
        convs.map(async (c) => {
          let messages = [];
          try {
            messages = (await getEmailThread(lead.id, c.conversation_id)) || [];
          } catch {
            messages = [];
          }
          // Oldest first inside a conversation — reads like an email thread
          const sorted = [...messages].sort(
            (a, b) =>
              new Date(a.sent_at || a.created_at).getTime() -
              new Date(b.sent_at || b.created_at).getTime(),
          );
          const last = sorted[sorted.length - 1];
          return {
            conversation_id: c.conversation_id,
            subject: c.subject || sorted[0]?.subject || "(No subject)",
            lastAt: last?.sent_at || last?.created_at || c.sent_at || c.created_at,
            messages: sorted,
          };
        }),
      );

      // Newest conversation first
      groups.sort(
        (a, b) =>
          new Date(b.lastAt || 0).getTime() - new Date(a.lastAt || 0).getTime(),
      );
      setInboxGroups(groups);

      if (convs.length >= 1 && convs[0].conversation_id) {
        // Keep the first conversation "active" for reply defaults
        setActiveConvId(convs[0].conversation_id);
        setThreadMessagesSorted(
          groups.find((g) => g.conversation_id === convs[0].conversation_id)
            ?.messages || [],
        );
        markEmailsRead(lead.id, convs[0].conversation_id).catch(() => {});
      }
    } catch {
      // leave lists as-is
    } finally {
      setConvLoading(false);
    }
  }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAllConversations();
  }, [loadAllConversations]);

  const loadThread = useCallback(async (convId) => {
    setActiveConvId(convId);
    setThreadLoading(true);
    setThreadMessages([]);
    try {
      const msgs = await getEmailThread(lead.id, convId);
      setThreadMessagesSorted(msgs);
      markEmailsRead(lead.id, convId).catch(() => {});
    } catch {
    } finally {
      setThreadLoading(false);
    }
  }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ref so focus/interval closures always see latest activeConvId without restarting the effect
  const _activeConvIdRef = useRef(null);
  _activeConvIdRef.current = activeConvId;

  // Redux SSE fan-out: refetch open thread when a matching inbound lead event arrives
  const lastInboxEvent = useSelector(selectLastInboxEvent);
  useEffect(() => {
    if (!lastInboxEvent || lastInboxEvent.type !== "inbound" || lastInboxEvent.inbox !== "lead") return;
    if (String(lastInboxEvent.lead_id) !== String(lead.id)) return;
    // Reload every conversation — the reply may belong to any of them, and the
    // Inbox tab renders them all inline.
    loadAllConversations();
  }, [lastInboxEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helper: find option_value from label ───────────────────────────────────
  const getValueByLabel = useCallback(
    (label) => {
      const opt = followUpOptionsList.find((o) => o.label === label);
      return opt ? opt.option_value : "0";
    },
    [followUpOptionsList],
  );

  // ── Helper: find label from option_value ───────────────────────────────────
  const getLabelByValue = useCallback(
    (optionValue) => {
      const opt = followUpOptionsList.find(
        (o) => String(o.option_value) === String(optionValue),
      );
      return opt ? opt.label : "Follow Up";
    },
    [followUpOptionsList],
  );

  // ── Set initial follow-up status from lead data once options load ──────────
  useMemo(() => {
    if (followUpOptionsList.length > 0 && lead.follow_up_status !== undefined) {
      const numericVal = String(lead.follow_up_status);
      const matchByValue = followUpOptionsList.find(
        (o) => String(o.option_value) === numericVal,
      );
      if (matchByValue) {
        setFollowUpStatus(matchByValue.label);
      } else {
        const matchByLabel = followUpOptionsList.find(
          (o) => o.label === lead.follow_up_status,
        );
        if (matchByLabel) setFollowUpStatus(matchByLabel.label);
      }
    }
  }, [followUpOptionsList, lead.follow_up_status]);

  // ── Fetch fresh lead details on open → resolve label from status ────────────
  React.useEffect(() => {
    if (!followUpOptionsList.length) return;
    fetchLeadDetails(lead.id)
      .then((res) => {
        if (res?.data) {
          const label =
            getLabelByValue(String(res.data.status)) || res.data.lead_stage;
          if (label) setFollowUpStatus(label);
          if (res.data.kanban_column) setLeadColumn(res.data.kanban_column);
          /* A demo booked or moved after this panel opened only shows up in the
             detail response, so the meeting block reads from there. */
          setDemoInfo({
            demo_meeting_link: res.data.demo_meeting_link,
            demo_date: res.data.demo_date,
            demo_time: res.data.demo_time,
            demo_status: res.data.demo_status,
            demo_mode: res.data.demo_mode,
            demo_meeting_organizer: res.data.demo_meeting_organizer,
          });
          setContact((prev) => ({
            ...prev,
            name: res.data.name || prev.name,
            email: res.data.email || prev.email,
            phone: res.data.phone || prev.phone,
            additional_phone:
              res.data.additional_phone ||
              res.data.phone2 ||
              prev.additional_phone ||
              "",
          }));
          // Update isLeadAssigned ref from fresh data
          if (res.data.assign_to != null) {
            isLeadAssigned.current = true;
          }
        }
      })
      .catch(() => {});
  }, [lead.id, followUpOptionsList, getLabelByValue]);

  const selectedTemplate = useMemo(() => {
    if (!emailTemplate) return null;
    return (
      emailTemplates.find((t) => String(t.id) === String(emailTemplate)) || null
    );
  }, [emailTemplate, emailTemplates]);

  const handleTemplateChange = useCallback(
    async (templateId) => {
      setEmailTemplate(templateId);
      if (!templateId) {
        setEmailSubject("");
        setEmailBody("");
        setTemplateAttachments([]);
        return;
      }
      try {
        const preview = await fetchTemplatePreview(templateId, lead.id);
        setEmailSubject(preview.subject || "");
        setEmailBody(preview.body || "");
        setTemplateAttachments(parseAttachments(preview.attachments));
      } catch {
        const tpl = emailTemplates.find(
          (t) => String(t.id) === String(templateId),
        );
        if (tpl) {
          setEmailSubject(tpl.subject || "");
          setEmailBody(tpl.body || "");
          setTemplateAttachments(parseAttachments(tpl.attachments));
        }
      }
    },
    [emailTemplates, lead.id],
  );

  const initials = (n) =>
    n
      ?.split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";
  const buildNextFU = (date, time) => {
    if (!date) return {};
    return {
      next_followup_date: date,
      ...(time ? { next_followup_time: time } : {}),
    };
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddCcEmail = () => {
    const v = emailCcInput.trim();
    if (!v) return;
    if (!isValidEmail(v)) {
      setEmailCcError("Invalid email address");
      return;
    }
    if (emailCcList.includes(v)) {
      setEmailCcError("Email already added");
      return;
    }
    setEmailCcList((p) => [...p, v]);
    setEmailCcInput("");
    setEmailCcError("");
  };

  const handleAddBccEmail = () => {
    const v = emailBccInput.trim();
    if (!v) return;
    if (!isValidEmail(v)) {
      setEmailBccError("Invalid email address");
      return;
    }
    if (emailBccList.includes(v)) {
      setEmailBccError("Email already added");
      return;
    }
    setEmailBccList((p) => [...p, v]);
    setEmailBccInput("");
    setEmailBccError("");
  };

  const handleSendEmail = async () => {
    if (!emailBody.replace(/<[^>]*>/g, "").trim() && !selectedTemplate) return;

    // ── Auto-assign guard: allocate unassigned lead before sending ──
    if (!isLeadAssigned.current) {
      const uid = parseInt(localStorage.getItem("trade_user_id"), 10);
      if (uid) {
        allocateLeadsMutation.mutate(
          { adminId: uid, leadIds: [lead.id] },
          {
            onSuccess: () => {
              isLeadAssigned.current = true;
              handleSendEmail();
            },
            onError: (err) =>
              toast.error(err?.message || "Failed to assign this lead to you"),
          },
        );
        return;
      }
    }

    const ccArr = emailCcList;
    const bccArr = emailBccList;
    const internalUserId = parseInt(localStorage.getItem("trade_user_id"), 10) || 1;

    setIsSendingEmail(true);
    try {
      if (selectedTemplate) {
        const allFiles = [...emailFiles];
        // `from` is not sent: the backend derives the sender from the CMS token,
        // so it is always the logged-in user (anything we send is ignored).
        const result = await sendLeadEmailWithTemplate({
          template_id: selectedTemplate.id,
          to: emailTo,
          lead_id: lead.id,
          internal_user_id: internalUserId,
          ...(replyConvId ? { conversation_id: replyConvId } : {}),
          ...(ccArr.length > 0 ? { cc: ccArr } : {}),
          ...(bccArr.length > 0 ? { bcc: bccArr } : {}),
          ...(allFiles.length > 0 ? { files: allFiles } : {}),
          ...(templateAttachments.length > 0 ? { existing_attachments: JSON.stringify(templateAttachments) } : {}),
        });
        toast.success(result?.message || "Email sent successfully!");
      } else {
        const result = await sendLeadEmail({
          to: emailTo,
          subject: emailSubject,
          body: emailBody,
          lead_id: lead.id,
          internal_user_id: internalUserId,
          ...(replyConvId ? { conversation_id: replyConvId } : {}),
          ...(ccArr.length > 0 ? { cc: ccArr } : {}),
          ...(bccArr.length > 0 ? { bcc: bccArr } : {}),
          ...(emailFiles.length > 0 ? { files: emailFiles } : {}),
        });
        toast.success(result?.message || "Email sent successfully!");
      }
      // Refresh conversations + their messages so the sent mail shows immediately
      loadAllConversations();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to send email");
      setIsSendingEmail(false);
      return;
    }

    // ── Log follow-up (unchanged logic) ──
    const stripHtml = (html) => {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    };

    const addFUPayload = selectedTemplate
      ? {
          lead_id: lead.id,
          followup_type: "email",
          followup_date: nowISO(),
          enquiry_status: emailFuStatus,
          direction: "outbound",
          delivery_status: "sent",
          recipient: emailTo,
          subject: emailSubject,
          content: emailBody
            ? stripHtml(
                emailBody
                  .replace(/<br\s*\/?>/gi, "\n")
                  .replace(/<\/p>/gi, "\n")
                  .replace(/<\/div>/gi, "\n")
                  .replace(/<\/h[1-6]>/gi, "\n")
                  .replace(/<\/li>/gi, "\n")
                  .replace(/<\/tr>/gi, "\n"),
              )
                .replace(/\n{3,}/g, "\n\n")
                .trim()
            : `Sent via template: ${selectedTemplate.template_name.replace(/_/g, " ")}`,
          template_used: selectedTemplate.template_name,
          remarks: emailRemarks || null,
          created_by: createdBy(),
          ...(ccArr.length > 0 ? { cc: ccArr } : {}),
          ...buildNextFU(emailNextDate, emailNextTime),
          attachments: emailFiles.length > 0 ? emailFiles : undefined,
          ...(templateAttachments.length > 0 ? { existing_attachments: JSON.stringify(templateAttachments) } : {}),
        }
      : {
          lead_id: lead.id,
          followup_type: "email",
          followup_date: nowISO(),
          enquiry_status: emailFuStatus,
          direction: "outbound",
          delivery_status: "sent",
          recipient: emailTo,
          subject: emailSubject,
          content: emailBody,
          template_used: null,
          remarks: emailRemarks || null,
          created_by: createdBy(),
          ...(ccArr.length > 0 ? { cc: ccArr } : {}),
          ...buildNextFU(emailNextDate, emailNextTime),
          attachments: emailFiles.length > 0 ? emailFiles : undefined,
        };

    addFollowUp.mutate(addFUPayload, {
      onSuccess: () => {
        autoAssignToSelf();
        if (shouldWriteStatus(emailFuStatus)) {
          knownStatusRef.current = emailFuStatus;
          updateEnquiryStatusSilent({ id: lead.id, status: emailFuStatus }).then(() => {
            queryClient.invalidateQueries({ queryKey: ["enquiryList"], refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: ["inbox-leads"], refetchType: "all" });
            queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "lead-followups" && String(q.queryKey[1]) === String(lead.id) });
          }).catch(() => {});
          setFollowUpStatus(getLabelByValue(emailFuStatus));
        }
        setReplyConvId(null);
        resetEmailForm();
        setActivePanel(null);
      },
      onError: () => {
        resetEmailForm();
        setActivePanel(null);
      },
      onSettled: () => setIsSendingEmail(false),
    });
  };

  const resetEmailForm = () => {
    setEmailBody("");
    setEmailBodyMode("compose");
    if (emailEditorRef.current) emailEditorRef.current.innerHTML = "";
    setEmailSubject("");
    setEmailTemplate("");
    setEmailFiles([]);
    setTemplateAttachments([]);
    setEmailRemarks("");
    setEmailCcList([]);
    setEmailCcInput("");
    setEmailBccList([]);
    setEmailBccInput("");
    setEmailNextDate("");
    setEmailNextTime("");
    setEmailFuStatus("0");
    setShowEmailCc(false);
    setShowEmailBcc(false);
  };

  const handleLogCall = () => {
    // ── Auto-assign guard: allocate unassigned lead before logging call ──
    if (!isLeadAssigned.current) {
      const uid = parseInt(localStorage.getItem("trade_user_id"), 10);
      if (uid) {
        allocateLeadsMutation.mutate(
          { adminId: uid, leadIds: [lead.id] },
          {
            onSuccess: () => {
              isLeadAssigned.current = true;
              handleLogCall();
            },
            onError: (err) =>
              toast.error(err?.message || "Failed to assign this lead to you"),
          },
        );
        return;
      }
    }

    addFollowUp.mutate(
      {
        lead_id: lead.id,
        followup_type: "call",
        followup_date: nowISO(),
        enquiry_status: callFuStatus,
        direction: "outbound",
        delivery_status: "delivered",
        call_outcome: callOutcome,
        call_duration: callDuration || null,
        content: callNote || `Call: ${callOutcome}`,
        remarks: callNote || null,
        subject: `Call — ${callOutcome}`,
        recipient: lead.phone,
        created_by: createdBy(),
        ...buildNextFU(callNextDate, callNextTime),
      },
      {
        onSuccess: () => {
          autoAssignToSelf();
          if (shouldWriteStatus(callFuStatus)) {
            knownStatusRef.current = callFuStatus;
            updateEnquiryStatusSilent({ id: lead.id, status: callFuStatus }).then(() => {
              queryClient.invalidateQueries({ queryKey: ["enquiryList"], refetchType: "all" });
              queryClient.invalidateQueries({ queryKey: ["inbox-leads"], refetchType: "all" });
              queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "lead-followups" && String(q.queryKey[1]) === String(lead.id) });
            }).catch(() => {});
            setFollowUpStatus(getLabelByValue(callFuStatus));
          }
          setCallNote("");
          setCallOutcome("Connected");
          setCallDuration("");
          setCallNextDate("");
          setCallNextTime("");
          setCallFuStatus("0");
          setActivePanel(null);
        },
      },
    );
  };

  const handleSaveNote = () => {
    if (!noteText.trim()) return;

    // ── Auto-assign guard: allocate unassigned lead before saving note ──
    if (!isLeadAssigned.current) {
      const uid = parseInt(localStorage.getItem("trade_user_id"), 10);
      if (uid) {
        allocateLeadsMutation.mutate(
          { adminId: uid, leadIds: [lead.id] },
          {
            onSuccess: () => {
              isLeadAssigned.current = true;
              handleSaveNote();
            },
            onError: (err) =>
              toast.error(err?.message || "Failed to assign this lead to you"),
          },
        );
        return;
      }
    }

    addFollowUp.mutate(
      {
        lead_id: lead.id,
        followup_type: "other",
        followup_date: nowISO(),
        direction: "outbound",
        content: noteText,
        remarks: noteText,
        created_by: createdBy(),
        ...buildNextFU(noteNextDate, noteNextTime),
      },
      {
        onSuccess: () => {
          autoAssignToSelf();
          /* A note does NOT make the lead "contacted" — it is an internal memo,
             the lead never heard from us. Only a sent mail or a logged call moves
             it out of New. */
          setNoteText("");
          setNoteNextDate("");
          setNoteNextTime("");
          setActivePanel(null);
        },
      },
    );
  };

  const addEmailFiles = (e) => {
    if (!e.target.files?.length) return;
    const newFiles = Array.from(e.target.files);
    if (emailFileRef.current) emailFileRef.current.value = "";
    setEmailFiles((prev) => [...prev, ...newFiles]);
  };

  React.useEffect(() => {
    if (
      activePanel === "email" &&
      emailFiles.length > 0 &&
      composeScrollRef.current
    ) {
      composeScrollRef.current.scrollTop =
        composeScrollRef.current.scrollHeight;
    }
  }, [emailFiles.length, activePanel]);
  const removeEmailFile = (idx) =>
    setEmailFiles((p) => p.filter((_, i) => i !== idx));
  const addWaFiles = (e) => {
    if (!e.target.files?.length) return;
    setWaFiles((prev) => [...prev, ...Array.from(e.target.files)]);
    if (waFileRef.current) waFileRef.current.value = "";
  };
  const removeWaFile = (idx) =>
    setWaFiles((p) => p.filter((_, i) => i !== idx));

  const handleEditSave = (payload) => {
    updateFollowUp.mutate(payload, {
      onSuccess: () => {
        setEditingFU(null);
        if (payload.enquiry_status != null) {
          const label = getLabelByValue(String(payload.enquiry_status));
          if (label) setFollowUpStatus(label);
        }
      },
    });
  };

  const handleDeleteFU = (fu) => {
    Swal.fire({
      title: "Delete this follow-up?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    }).then((r) => {
      if (r.isConfirmed) deleteFollowUp.mutate({ id: fu.id, lead_id: lead.id });
    });
  };

  const syncLeadStage = (statusLabel) => {
    const optionValue = getValueByLabel(statusLabel);
    if (!optionValue || optionValue === "0") return;
    updateEnquiryStatus.mutate(
      { id: lead.id, status: Number(optionValue), message: "" },
      { onSuccess: () => setFollowUpStatus(statusLabel) },
    );
  };

  const handleStatusDropdownChange = (e) => {
    const v = e.target.value;
    if (v === followUpStatus) return;
    setPendingStatus(v);
    setShowStageModal(true);
  };

  const handleStageConfirm = (payload) => {
    const selectedLabel = payload.follow_up_status;
    const optionValue = getValueByLabel(selectedLabel);

    updateEnquiryStatus.mutate(
      {
        id: lead.id,
        status: Number(optionValue),
        message: payload.remarks || "",
      },
      {
        onSuccess: () => {
          setFollowUpStatus(selectedLabel);
          setShowStageModal(false);
          setPendingStatus(null);
        },
        onError: () => {
          setShowStageModal(false);
          setPendingStatus(null);
        },
      },
    );
  };

  const saveContact = async (d) => {
    setIsSavingContact(true);
    try {
      await updateLeadContact(lead.id, {
        name: d.name,
        email: d.email,
        phone: d.phone,
        additional_phone: parsePhones(d.additional_phone),
      });
      setContact(d);
      setShowContactEdit(false);
      toast.success("Contact updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["enquiryList"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["inbox-leads"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["future-leads"] });
      queryClient.invalidateQueries({ queryKey: ["done-leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead-details", lead.id] });
    } catch (err) {
      toast.error(err?.message || "Failed to update contact");
    } finally {
      setIsSavingContact(false);
    }
  };

  const PANELS = [
    {
      key: "email",
      label: "Compose Mail",
      color: "#667eea",
      grad: "linear-gradient(135deg,#667eea,#764ba2)",
      shadow: "rgba(102,126,234,0.35)",
      Icon: IC.Mail,
    },
    {
      key: "call",
      label: "Call",
      color: "#f59e0b",
      grad: "linear-gradient(135deg,#f59e0b,#d97706)",
      shadow: "rgba(245,158,11,0.3)",
      Icon: IC.Phone,
    },
    {
      key: "note",
      label: "Writer Note",
      color: "#8b5cf6",
      grad: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
      shadow: "rgba(139,92,246,0.3)",
      Icon: IC.Note,
    },
  ];

  const fuColor = statusColor(followUpStatus);
  const isSaving = addFollowUp.isPending;
  const isAllocating = allocateLeadsMutation.isPending;
  const isTemplateSending = isSendingEmail;
  const L = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#6b7280",
    display: "block",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  };
  const inp = "fu-inp";

  return (
    <div
      style={{
        fontFamily: "'Segoe UI',system-ui,sans-serif",
        background: "#f1f5f9",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{`
        .fu-i{transition:all 0.18s ease;cursor:pointer}.fu-i:hover{transform:translateY(-1px)}
        .fu-ab{display:inline-flex;align-items:center;gap:7px;padding:8px 18px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;border:none;transition:all 0.18s}.fu-ab:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(0,0,0,0.15)}
        .fu-inp{width:100%;padding:9px 12px;border:1.5px solid #e2e8f0;border-radius:9px;font-size:13px;outline:none;transition:border 0.2s;background:white;box-sizing:border-box}.fu-inp:focus{border-color:#667eea;box-shadow:0 0 0 3px rgba(102,126,234,0.1)}
        .fu-sel{width:100%;padding:9px 12px;border:1.5px solid #e2e8f0;border-radius:9px;font-size:13px;outline:none;background:white;cursor:pointer;box-sizing:border-box}
        .fu-ta{width:100%;padding:10px 12px;border:1.5px solid #e2e8f0;border-radius:9px;font-size:13px;outline:none;resize:vertical;min-height:80px;background:white;font-family:inherit;box-sizing:border-box}.fu-ta:focus{border-color:#667eea;box-shadow:0 0 0 3px rgba(102,126,234,0.1)}
        .fu-card{border-radius:14px;padding:14px 16px;border:1.5px solid;transition:all 0.2s}.fu-card:hover{transform:translateX(3px);box-shadow:0 6px 20px rgba(0,0,0,0.07)}
        @keyframes slideUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}.fu-anim{animation:slideUp 0.25s ease}
        .fu-pill{padding:5px 12px;border-radius:20px;font-size:12px;font-weight:600;border:1.5px solid;cursor:pointer;transition:all 0.15s}.fu-pill:hover{transform:translateY(-1px)}
        ::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#c7d2fe;border-radius:10px}
        .fu-section{border:1.5px solid #e8ecff;border-radius:14px;overflow:hidden;margin-bottom:0}
        .fu-sec-hdr{background:linear-gradient(135deg,#f8f9ff,#f0f4ff);padding:11px 14px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #e8ecff}
        .fu-nfd{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:10px;padding-top:10px;border-top:1px dashed #e2e8f0}
        .fu-nfd label{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:3px;display:flex;align-items:center;gap:3px}
        .fu-nfd input{padding:7px 10px;border:1.5px solid #e2e8f0;border-radius:7px;font-size:12px;outline:none;background:white;width:100%;box-sizing:border-box}
        .fu-files-row{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
        @keyframes modalIn{from{opacity:0;transform:scale(0.92) translateY(14px)}to{opacity:1;transform:scale(1) translateY(0)}}
      `}</style>

      <input
        type="file"
        multiple
        ref={emailFileRef}
        onChange={addEmailFiles}
        style={{ display: "none" }}
      />
      <input
        type="file"
        multiple
        ref={waFileRef}
        onChange={addWaFiles}
        style={{ display: "none" }}
      />

      {showStageModal && (
        <StageUpdateModal
          currentStatus={followUpStatus}
          initialStatus={pendingStatus}
          onSave={handleStageConfirm}
          onCancel={() => {
            setShowStageModal(false);
            setPendingStatus(null);
          }}
          isLoading={updateEnquiryStatus.isPending}
          followUpOptionsList={followUpOptionsList}
        />
      )}
      {editingFU && (
        <EditFollowUpModal
          fu={editingFU}
          onSave={handleEditSave}
          onCancel={() => setEditingFU(null)}
          isLoading={updateFollowUp.isPending}
          followUpOptionsList={followUpOptionsList}
        />
      )}
      {showContactEdit && (
        <ContactEditModal
          contact={contact}
          onSave={saveContact}
          onCancel={() => setShowContactEdit(false)}
          isLoading={isSavingContact}
        />
      )}
      {showPreview && selectedTemplate && (
        <EmailPreviewModal
          subject={selectedTemplate.subject}
          body={selectedTemplate.body}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* BREADCRUMB */}
      <div
        style={{
          padding: "0 24px",
          background: "white",
          borderBottom: "1px solid #e8ecff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "13px",
          color: "#6b7280",
          flexShrink: 0,
          height: "48px",
        }}
      >
        <button
          onClick={onClose}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            background: "none",
            border: "none",
            color: "#667eea",
            fontWeight: 600,
            cursor: "pointer",
            fontSize: "13px",
            padding: 0,
          }}
        >
          <IC.ArrowLeft /> Back
        </button>
        <span style={{ color: "#c7d2fe", fontSize: "16px" }}>›</span>
        <span style={{ color: "#1f2937", fontWeight: 600 }}>{lead.name}</span>
        <span style={{ color: "#9ca3af" }}>[ID: {lead.id}]</span>
        {lead.stage_name && (
          <span
            style={{
              marginLeft: "6px",
              padding: "2px 10px",
              borderRadius: "20px",
              background: "#f0f4ff",
              color: "#667eea",
              fontSize: "11px",
              fontWeight: 700,
              border: "1px solid #c7d2fe",
            }}
          >
            {lead.stage_name}
          </span>
        )}
      </div>

      {/* TWO-COLUMN LAYOUT */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "300px 1fr",
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {/* ══ LEFT SIDEBAR ══ */}
        <div
          style={{
            background: "white",
            borderRight: "2px solid #f0f4ff",
            overflowY: "auto",
            padding: "16px 14px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            minHeight: 0,
            height: "100%",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg,#667eea,#764ba2)",
              borderRadius: "16px",
              padding: "18px 16px",
              color: "white",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "14px",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "17px",
                  fontWeight: 700,
                  flexShrink: 0,
                  border: "2px solid rgba(255,255,255,0.4)",
                }}
              >
                {initials(lead.name)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "16px", fontWeight: 700 }}>
                  {lead.name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    opacity: 0.8,
                    marginTop: "2px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lead.message || lead.service || "Enquiry"}
                </div>
              </div>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  fontSize: "12px",
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "8px",
                  padding: "7px 10px",
                }}
              >
                <IC.Mail />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lead.email || "N/A"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  fontSize: "12px",
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "8px",
                  padding: "7px 10px",
                }}
              >
                <IC.Phone />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{withCountryCode(lead.country_code, lead.phone) || "N/A"}</div>
                  {/* Every alternate the lead has — the card stays one line
                      per number and the dial menu below lists the same set. */}
                  {parsePhones(lead.additional_phone).map((alt) => (
                    <div key={alt} style={{ fontSize: "11px", opacity: 0.8, marginTop: "1px" }}>{alt}</div>
                  ))}
                </div>
                {lead.phone && (
                  <span style={lockedStyle}>
                  {parsePhones(lead.additional_phone).length > 0 ? (
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: [
                          { key: "primary", label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}><IC.Phone /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Primary</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{withCountryCode(lead.country_code, lead.phone)}</div></div></div>) },
                          ...parsePhones(lead.additional_phone).map((alt, i, arr) => ({
                            key: `alt-${alt}`,
                            label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563eb" }}><IC.Phone /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>{arr.length > 1 ? `Alternate ${i + 1}` : "Alternate"}</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{alt}</div></div></div>),
                          })),
                        ],
                        onClick: ({ key }) => {
                          const num = key === "primary" ? lead.phone : key.replace(/^alt-/, "");
                          handleClickToCall(dialNumber(lead.country_code, num), lead.name).then(
                            (placed) => placed && setActivePanel("call"),
                          );
                        },
                      }}
                    >
                      <button className="fu-i" style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#16a34a", border: "none", display: "flex", alignItems: "center", justifyContent: "center", color: "white", cursor: "pointer", flexShrink: 0 }}>
                        <IC.Phone />
                      </button>
                    </Dropdown>
                  ) : (
                    <button className="fu-i" onClick={() => { handleClickToCall(dialNumber(lead.country_code, lead.phone), lead.name).then((placed) => placed && setActivePanel("call")); }} style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#16a34a", border: "none", display: "flex", alignItems: "center", justifyContent: "center", color: "white", cursor: "pointer", flexShrink: 0 }}>
                      <IC.Phone />
                    </button>
                  )}
                  </span>
                )}
              </div>
              {lead.company && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    fontSize: "12px",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    padding: "7px 10px",
                  }}
                >
                  <IC.Building />
                  {lead.company}
                </div>
              )}
              {lead.address && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    fontSize: "12px",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    padding: "7px 10px",
                  }}
                >
                  <IC.MapPin />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {lead.address}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Demo meeting — only for a lead that actually has one booked. The
              join link is the thing a rep needs at the top of the hour, so it
              sits above the follow-up list rather than inside the timeline. */}
          {demoLead.demo_meeting_link && (
            <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid #ddd6fe", background: "#fff", boxShadow: "0 2px 12px rgba(124,58,237,0.08)", marginBottom: 14 }}>
              <div style={{ background: "linear-gradient(135deg,#7c3aed15,#5b48e810)", borderBottom: "1px solid #ede9fe", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#5b21b6" }}>📅 Demo</span>
                {demoLead.demo_status && <DemoStatusPill lead={demoLead} size={10} />}
              </div>
              <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                  {formatDemoWhen(demoLead)}
                </div>
                <a
                  href={demoLead.demo_meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px 14px", borderRadius: 10, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", fontSize: 12.5, fontWeight: 700, textDecoration: "none" }}
                >
                  Join Teams Meeting
                </a>
                {demoLead.demo_meeting_organizer && (
                  /* Which mailbox the invite went out from — the lead will reply
                     to that address, not to whoever is reading this. */
                  <div style={{ fontSize: 11, color: "#9ca3af", wordBreak: "break-all" }}>
                    Invite sent from: {demoLead.demo_meeting_organizer}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="fu-section" style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid #e8ecff", background: "#fff", boxShadow: "0 2px 12px rgba(102,126,234,0.07)" }}>
            <div className="fu-sec-hdr" style={{ background: "linear-gradient(135deg,#667eea15,#764ba210)", borderBottom: "1px solid #e8ecff", padding: "10px 14px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#4338ca",
                }}
              >
                <span style={{ color: "#667eea", fontSize: "13px" }}><IC.Calendar /></span>
                Scheduled Follow-Ups
                <span
                  style={{
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    color: "white",
                    borderRadius: "20px",
                    padding: "1px 8px",
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.3px",
                  }}
                >
                  {allFollowUps.filter(
                    (fu) =>
                      // Any follow-up carrying a date belongs here — a note
                      // (saved as followup_type "other") schedules the next
                      // touch just like a call or a mail does.
                      Boolean(fu.next_followup_date),
                  ).length > 0
                    ? 1
                    : 0}
                </span>
              </div>
            </div>
            <div
              style={{
                padding: "8px 12px 10px",
                maxHeight: "280px",
                overflowY: "auto",
              }}
            >
              {allFuLoading && (
                <p
                  style={{
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: "12px",
                    padding: "10px 0",
                  }}
                >
                  Loading...
                </p>
              )}
              {(() => {
                const scheduledFollowUps = allFollowUps
                  .filter(
                    (fu) =>
                      // Any follow-up carrying a date belongs here — a note
                      // (saved as followup_type "other") schedules the next
                      // touch just like a call or a mail does.
                      Boolean(fu.next_followup_date),
                  )
                  .slice(0, 1);

                if (!allFuLoading && scheduledFollowUps.length === 0) {
                  return (
                    <div style={{ textAlign: "center", padding: "18px 0 10px" }}>
                      <div style={{ fontSize: "22px", marginBottom: "6px", opacity: 0.3 }}>📅</div>
                      <p style={{ margin: 0, color: "#9ca3af", fontSize: "12px" }}>
                        No scheduled follow-ups yet.
                      </p>
                    </div>
                  );
                }
                return (
                  !allFuLoading &&
                  scheduledFollowUps.map((fu, i) => {
                    const cfg = getTypeCfg(fu.followup_type);
                    return (
                      <div
                        key={fu.id}
                        style={{
                          padding: "10px 4px 8px",
                          borderBottom:
                            i < scheduledFollowUps.length - 1
                              ? "1px solid #f0f4ff"
                              : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "flex-start",
                          }}
                        >
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "10px",
                              background:
                                cfg.color === "#667eea"
                                  ? "linear-gradient(135deg,#667eea,#764ba2)"
                                  : `linear-gradient(135deg,${cfg.color},${cfg.color}cc)`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "11px",
                              flexShrink: 0,
                              marginTop: "1px",
                              boxShadow: `0 3px 8px ${cfg.color}40`,
                            }}
                          >
                            <cfg.Icon />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#1f2937",
                                lineHeight: 1.4,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {fu.subject ||
                                fu.remarks ||
                                fu.content?.slice(0, 60) ||
                                cfg.label}
                            </p>
                            <div
                              style={{
                                display: "flex",
                                gap: "5px",
                                marginTop: "5px",
                                flexWrap: "wrap",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  padding: "2px 9px",
                                  borderRadius: "20px",
                                  background: cfg.bg,
                                  color: cfg.color,
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  border: `1px solid ${cfg.border}`,
                                }}
                              >
                                {cfg.label}
                              </span>
                              {fu.enquiry_status != null &&
                                (() => {
                                  const statusLabel = getLabelByValue(
                                    String(fu.enquiry_status),
                                  );
                                  const c = statusColor(statusLabel);
                                  return (
                                    <span
                                      style={{
                                        padding: "2px 9px",
                                        borderRadius: "20px",
                                        background: `${c}15`,
                                        color: c,
                                        fontSize: "10px",
                                        fontWeight: 700,
                                        border: `1px solid ${c}30`,
                                      }}
                                    >
                                      {statusLabel}
                                    </span>
                                  );
                                })()}
                            </div>
                            {fu.next_followup_date && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "11px",
                                  color: "#f59e0b",
                                  fontWeight: 600,
                                  marginTop: "6px",
                                  background: "#fffbeb",
                                  border: "1px solid #fde68a",
                                  borderRadius: "8px",
                                  padding: "2px 8px",
                                }}
                              >
                                <IC.Clock /> Next: {formatNextFollowUp(fu)}
                              </span>
                            )}
                          </div>
                          {/* Editing a scheduled follow-up rewrites the lead's
                              status everywhere, so it obeys the same gate as the
                              compose actions: not ours, or closed as negative,
                              means read-only. */}
                          <div
                            style={{
                              ...lockedStyle,
                              display: "flex",
                              gap: "4px",
                              flexShrink: 0,
                            }}
                          >
                            <Tooltip title="Edit">
                              <button
                                className="fu-i"
                                onClick={() => setEditingFU(fu)}
                                style={{
                                  width: "26px",
                                  height: "26px",
                                  borderRadius: "8px",
                                  border: "1.5px solid #e8ecff",
                                  background: "#f8f9ff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#667eea",
                                  cursor: "pointer",
                                }}
                              >
                                <IC.Edit />
                              </button>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <button
                                className="fu-i"
                                onClick={() => handleDeleteFU(fu)}
                                style={{
                                  width: "26px",
                                  height: "26px",
                                  borderRadius: "8px",
                                  border: "1.5px solid #fecaca",
                                  background: "#fef2f2",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#dc2626",
                                  cursor: "pointer",
                                }}
                              >
                                <IC.Trash />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    );
                  })
                );
              })()}
            </div>
          </div>

          <div className="fu-section">
            <div className="fu-sec-hdr">
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#1f2937",
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                }}
              >
                <IC.User /> Contact
              </div>
            </div>
            <div style={{ padding: "12px 14px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#1f2937",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {contact.name}
                  </p>
                  <p
                    style={{
                      margin: "3px 0 0",
                      fontSize: "12px",
                      color: "#6b7280",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {contact.email}
                  </p>
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: "12px",
                      color: "#6b7280",
                    }}
                  >
                    {withCountryCode(lead.country_code, contact.phone)}
                  </p>
                  {/* One line per alternate. Rendering the raw value would join
                      an array of numbers into one unreadable digit string. */}
                  {parsePhones(contact.additional_phone).map((alt) => (
                    <p
                      key={alt}
                      style={{
                        margin: "2px 0 0",
                        fontSize: "12px",
                        color: "#6b7280",
                      }}
                    >
                      {alt}{" "}
                      <span style={{ fontSize: "10px", color: "#9ca3af" }}>
                        (alt)
                      </span>
                    </p>
                  ))}
                </div>
                <div
                  style={{
                    ...lockedStyle,
                    display: "flex",
                    gap: "4px",
                    flexShrink: 0,
                    alignSelf: "flex-start",
                    marginTop: "2px",
                  }}
                >
                  {contact.additional_phone ? (
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: [
                          { key: "primary", label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}><IC.Phone /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Primary</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{withCountryCode(lead.country_code, contact.phone)}</div></div></div>) },
                          { key: "alternate", label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563eb" }}><IC.Phone /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Alternate</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{contact.additional_phone}</div></div></div>) },
                        ],
                        onClick: ({ key }) => {
                          const num = key === "primary" ? contact.phone : contact.additional_phone;
                          handleClickToCall(dialNumber(lead.country_code, num), contact.name).then(
                            (placed) => placed && setActivePanel("call"),
                          );
                        },
                      }}
                    >
                      <button className="fu-i" style={{ width: "26px", height: "26px", borderRadius: "7px", border: "1.5px solid #e8ecff", background: "#f8f9ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#667eea", cursor: "pointer", flexShrink: 0 }}>
                        <IC.Phone />
                      </button>
                    </Dropdown>
                  ) : (
                    <Tooltip title="Call">
                      <button className="fu-i" onClick={() => { handleClickToCall(dialNumber(lead.country_code, contact.phone), contact.name).then((placed) => placed && setActivePanel("call")); }} style={{ width: "26px", height: "26px", borderRadius: "7px", border: "1.5px solid #e8ecff", background: "#f8f9ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#667eea", cursor: "pointer", flexShrink: 0 }}>
                        <IC.Phone />
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip title="Email">
                    <button
                      className="fu-i"
                      onClick={() => {
                        setActivePanel("email");
                      }}
                      style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "7px",
                        border: "1.5px solid #e8ecff",
                        background: "#f8f9ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#667eea",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      <IC.Mail />
                    </button>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <button
                      className="fu-i"
                      onClick={() => setShowContactEdit(true)}
                      style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "7px",
                        border: "1.5px solid #e8ecff",
                        background: "#f8f9ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#667eea",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      <IC.Edit />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "4px 2px 0",
              marginTop: "2px",
            }}
          >
            <button
              onClick={isNegativeLead ? handleMarkPositive : handleMarkNegative}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "none",
                border: "none",
                color: isNegativeLead ? "#16a34a" : "#ef4444",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <IC.Negative /> {isNegativeLead ? "Mark Positive" : "Negative Closed"}
            </button>
          </div>
        </div>

        {/* ══ RIGHT PANEL ══ */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "12px 22px",
              borderBottom: "2px solid #f0f4ff",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            {!isMyLead && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "12px", padding: "8px 14px", width: "100%" }}>
                <span style={{ fontSize: "13px", color: "#92400e" }}>
                  Assigned to <b>{lead.assignedToName || "another user"}</b> — take it over to follow up, so your name and email go out on it.
                </span>
                {mayTakeOver ? (
                  <button
                    onClick={handleTakeOver}
                    disabled={takingOver}
                    style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "7px", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", border: "none", borderRadius: "10px", padding: "7px 16px", fontSize: "13px", fontWeight: 700, cursor: takingOver ? "not-allowed" : "pointer", opacity: takingOver ? 0.7 : 1, whiteSpace: "nowrap" }}
                  >
                    <IC.User /> {takingOver ? "Taking over…" : "Take over lead"}
                  </button>
                ) : (
                  <span style={{ marginLeft: "auto", fontSize: "12px", color: "#b45309", fontStyle: "italic" }}>
                    Only a manager or admin can take over a lead.
                  </span>
                )}
              </div>
            )}
            {/* The panel is still reachable from the Negative list so the history
                can be read — this says why nothing is clickable, and offers the
                one action that is. Shown only when the lead is ours; otherwise
                the take-over notice above already explains the lock. */}
            {isMyLead && isNegativeLead && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "12px", padding: "8px 14px", width: "100%" }}>
                <span style={{ fontSize: "13px", color: "#991b1b" }}>
                  This lead is marked <b>Negative</b> — follow-ups are closed. Mark it positive to call, mail or convert it again.
                </span>
                <button
                  onClick={handleMarkPositive}
                  style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "7px", background: "linear-gradient(135deg,#16a34a,#15803d)", color: "#fff", border: "none", borderRadius: "10px", padding: "7px 16px", fontSize: "13px", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  <IC.Negative /> Mark Positive
                </button>
              </div>
            )}
            {PANELS.map(({ key, label, color, grad, shadow, Icon }) => {
              const btnStyle = {
                display: "inline-flex", alignItems: "center", gap: "7px", padding: "8px 16px",
                borderRadius: "22px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
                border: `2px solid ${color}25`, whiteSpace: "nowrap",
                background: activePanel === key ? grad : `${color}10`,
                color: activePanel === key ? "white" : color,
                boxShadow: activePanel === key ? `0 4px 14px ${shadow}` : "none",
                transition: "all 0.2s",
                // Locked until the lead belongs to the logged-in user, and while
                // it sits in Negative — a closed lead is not worked.
                ...(canAct ? {} : { opacity: 0.45, cursor: "not-allowed", pointerEvents: "none" }),
              };
              if (key === "call" && lead.additional_phone) {
                return (
                  <Dropdown
                    key={key}
                    trigger={["click"]}
                    menu={{
                      items: [
                        { key: "primary", label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}><IC.Phone /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Primary</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{withCountryCode(lead.country_code, lead.phone)}</div></div></div>) },
                        { key: "alternate", label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563eb" }}><IC.Phone /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Alternate</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{lead.additional_phone}</div></div></div>) },
                      ],
                      onClick: ({ numKey }) => {
                        const num = numKey === "primary" ? lead.phone : lead.additional_phone;
                        handleClickToCall(dialNumber(lead.country_code, num), lead.name).then(
                          (placed) => placed && setActivePanel("call"),
                        );
                      },
                    }}
                  >
                    <button className="fu-i" onClick={() => setActivePanel(activePanel === key ? null : key)} style={btnStyle}>
                      <Icon />{label}
                    </button>
                  </Dropdown>
                );
              }
              return (
                <button
                  key={key}
                  className="fu-i"
                  onClick={() => {
                    if (key === "call") {
                      // No dialer, no call log: the form opens only once the call
                      // has actually been placed.
                      handleClickToCall(dialNumber(lead.country_code, lead.phone), lead.name).then(
                        (placed) => placed && setActivePanel("call"),
                      );
                      return;
                    }
                    setActivePanel(activePanel === key ? null : key);
                  }}
                  style={btnStyle}
                >
                  <Icon />
                  {label}
                </button>
              );
            })}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 11px",
                  borderRadius: "20px",
                  background: `${fuColor}15`,
                  color: fuColor,
                  fontSize: "11px",
                  fontWeight: 700,
                  border: `1.5px solid ${fuColor}30`,
                  whiteSpace: "nowrap",
                }}
              >
                <IC.Layers />
                {followUpStatus}
              </span>
              {/* Global follow-up status — set the stage without opening a compose
                  panel. "Converted" is not offered here: a lead reaches Done only
                  by creating a subscription. */}
              <div style={{ position: "relative", ...(canAct ? {} : { opacity: 0.4, pointerEvents: "none" }) }}>
                <select
                  value={followUpStatus}
                  onChange={handleStatusDropdownChange}
                  disabled={optionsLoading}
                  style={{
                    minWidth: "145px",
                    padding: "7px 32px 7px 12px",
                    border: `1.5px solid ${fuColor}`,
                    borderRadius: "9px",
                    fontSize: "13px",
                    outline: "none",
                    background: "white",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    color: fuColor,
                    fontWeight: 700,
                    appearance: "none",
                    WebkitAppearance: "none",
                  }}
                >
                  {optionsLoading && <option>Loading...</option>}
                  {followUpOptionsList.map((opt) => (
                    <option key={opt.id} value={opt.label}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: fuColor,
                    pointerEvents: "none",
                  }}
                >
                  <IC.ChevronDown />
                </div>
              </div>

              {/* Status is set while logging a call / mail / note. What belongs
                  here is the next step: turning the lead into a customer. The
                  lead moves to Done once that subscription exists. */}
              {/* A quote is the step before a subscription, so it starts from
                  the same place — and carries what the lead already told us
                  rather than making the rep retype it. */}
              {[
                { doc: "proposal", to: "/cms/proposals/create", label: "Create Proposal", tint: "#ede9fe", line: "#ddd6fe", ink: "#5b48e8" },
                { doc: "quotation", to: "/cms/quotations/create", label: "Create Quotation", tint: "#f0fdfa", line: "#99f6e4", ink: "#0d9488" },
              ].map(({ doc, to, label, tint, line, ink }) => (
                <button
                  key={doc}
                  onClick={() => navigate(to, { state: { payload: quotePayload() } })}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "7px 16px",
                    border: `1.5px solid ${line}`,
                    borderRadius: "9px",
                    background: tint,
                    color: ink,
                    fontSize: "13px",
                    fontWeight: 700,
                    cursor: canAct ? "pointer" : "not-allowed",
                    whiteSpace: "nowrap",
                    ...(canAct ? {} : { opacity: 0.4, pointerEvents: "none" }),
                  }}
                  title={
                    canAct
                      ? undefined
                      : isNegativeLead
                        ? "Mark this lead positive first"
                        : "Take over this lead first"
                  }
                >
                  <IC.Doc /> {label}
                </button>
              ))}

              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  // The backend closes THIS lead once the order is created.
                  if (lead.id) params.set("lead_id", String(lead.id));
                  if (lead.name) params.set("name", lead.name);
                  if (lead.email) params.set("email", lead.email);
                  const primary = contact?.phone || lead.phone;
                  if (primary) params.set("phone", primary);
                  // Leads are global — without the code the customer's number is
                  // ambiguous and would silently default to +91.
                  if (lead.country_code) params.set("country_code", lead.country_code);
                  // A lead can hold several numbers — send them all so Add
                  // Customer can let the user pick which one the customer uses.
                  // contact holds the freshest numbers (it is refreshed after an
                  // edit); lead is the fallback for a straight page load.
                  const altNums = parsePhones(
                    contact?.additional_phone ?? lead.additional_phone,
                  );
                  if (altNums.length) params.set("additional_phone", altNums.join(","));
                  if (lead.company) params.set("company", lead.company);
                  if (lead.address) params.set("address", lead.address);
                  // Attribution travels with the lead — Add Customer needs it,
                  // and it is lost forever once the lead becomes a customer.
                  if (lead.utm_source) params.set("utm_source", lead.utm_source);
                  if (lead.utm_medium) params.set("utm_medium", lead.utm_medium);
                  if (lead.utm_campaign) params.set("utm_campaign", lead.utm_campaign);
                  navigate(`/cms/manageUserData/addUser?${params.toString()}`);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "7px 16px",
                  border: "1.5px solid #bbf7d0",
                  borderRadius: "9px",
                  background: "#f0fdf4",
                  color: "#16a34a",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: canAct ? "pointer" : "not-allowed",
                  whiteSpace: "nowrap",
                  // Converting someone else's lead would put the customer (and the
                  // subscription) under your name while the lead stays theirs.
                  // A negative lead has to be reopened before it can convert.
                  ...(canAct ? {} : { opacity: 0.4, pointerEvents: "none" }),
                }}
                title={
                  canAct
                    ? undefined
                    : isNegativeLead
                      ? "Mark this lead positive first"
                      : "Take over this lead first"
                }
              >
                <IC.User /> Create Subscription
              </button>
            </div>
          </div>

          {/* COMPOSE PANELS */}
          {activePanel && (
            <div
              style={{ padding: "0 22px", marginTop: "14px", flexShrink: 0 }}
            >
              <div
                className="fu-anim"
                style={{
                  background: "white",
                  borderRadius: "15px",
                  border: `2px solid ${getTypeCfg(activePanel).border}`,
                  overflow: "hidden",
                  boxShadow: `0 5px 24px ${getTypeCfg(activePanel).color}12`,
                  marginBottom: "4px",
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: "calc(100vh - 200px)",
                }}
              >
                <div
                  style={{
                    background: PANELS.find((p) => p.key === activePanel).grad,
                    padding: "11px 18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "9px",
                    }}
                  >
                    <div
                      style={{
                        width: "30px",
                        height: "30px",
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                      }}
                    >
                      {React.createElement(
                        PANELS.find((p) => p.key === activePanel).Icon,
                      )}
                    </div>
                    <span
                      style={{
                        color: "white",
                        fontWeight: 700,
                        fontSize: "14px",
                      }}
                    >
                      {PANELS.find((p) => p.key === activePanel).label}
                    </span>
                    {activePanel === "call" && (
                      <span
                        style={{
                          color: "rgba(255,255,255,.7)",
                          fontSize: "11px",
                        }}
                      >
                        {lead.name} ({withCountryCode(lead.country_code, lead.phone)})
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setActivePanel(null)}
                    style={{
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <IC.X />
                  </button>
                </div>
                <div
                  ref={composeScrollRef}
                  style={{
                    padding: "16px 18px",
                    overflowY: "auto",
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {activePanel === "email" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "11px",
                      }}
                    >
                      <div
                        style={{
                          border: "1.5px solid #e2e8f0",
                          borderRadius: "9px",
                          overflow: "hidden",
                          background: "white",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            borderBottom:
                              showEmailCc || showEmailBcc
                                ? "1px solid #f0f0f0"
                                : "none",
                          }}
                        >
                          <span
                            style={{
                              padding: "9px 12px",
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#6b7280",
                              minWidth: "48px",
                              flexShrink: 0,
                              borderRight: "1px solid #f0f0f0",
                            }}
                          >
                            To
                          </span>
                          <input
                            style={{
                              flex: 1,
                              padding: "9px 12px",
                              border: "none",
                              outline: "none",
                              fontSize: "13px",
                              background: "transparent",
                            }}
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            placeholder="recipient@email.com"
                          />
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              padding: "0 12px",
                              flexShrink: 0,
                            }}
                          >
                            {!showEmailCc && (
                              <button
                                type="button"
                                onClick={() => setShowEmailCc(true)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  cursor: "pointer",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  transition: "color 0.15s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.color = "#1f2937")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.color = "#6b7280")
                                }
                              >
                                Cc
                              </button>
                            )}
                            {!showEmailBcc && (
                              <button
                                type="button"
                                onClick={() => setShowEmailBcc(true)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  cursor: "pointer",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  transition: "color 0.15s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.color = "#1f2937")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.color = "#6b7280")
                                }
                              >
                                Bcc
                              </button>
                            )}
                          </div>
                        </div>
                        {showEmailCc && (
                          <div
                            style={{
                              borderBottom: showEmailBcc
                                ? "1px solid #f0f0f0"
                                : "none",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                              }}
                            >
                              <span
                                style={{
                                  padding: "10px 12px",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  minWidth: "48px",
                                  flexShrink: 0,
                                  borderRight: "1px solid #f0f0f0",
                                }}
                              >
                                Cc
                              </span>
                              <div
                                style={{
                                  flex: 1,
                                  padding: "5px 8px",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                  alignItems: "center",
                                }}
                              >
                                {emailCcList.map((e, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "3px",
                                      padding: "2px 10px",
                                      background: "#eff6ff",
                                      border: "1px solid #bfdbfe",
                                      borderRadius: "999px",
                                      fontSize: "12px",
                                      color: "#2563eb",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {e}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setEmailCcList((p) =>
                                          p.filter((_, j) => j !== i),
                                        )
                                      }
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#93c5fd",
                                        fontSize: "16px",
                                        lineHeight: 1,
                                        padding: "0 0 0 3px",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      &times;
                                    </button>
                                  </span>
                                ))}
                                <input
                                  autoFocus
                                  style={{
                                    flex: 1,
                                    minWidth: "160px",
                                    padding: "4px 2px",
                                    border: "none",
                                    outline: "none",
                                    fontSize: "13px",
                                    background: "transparent",
                                  }}
                                  value={emailCcInput}
                                  onChange={(e) => {
                                    setEmailCcInput(e.target.value);
                                    setEmailCcError("");
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                      e.preventDefault();
                                      handleAddCcEmail();
                                    }
                                  }}
                                  onBlur={handleAddCcEmail}
                                  placeholder={
                                    emailCcList.length === 0
                                      ? "cc@email.com (press Enter to add)"
                                      : ""
                                  }
                                />
                              </div>
                            </div>
                            {emailCcError && (
                              <div
                                style={{
                                  padding: "2px 12px 5px 60px",
                                  fontSize: "11px",
                                  color: "#ef4444",
                                }}
                              >
                                {emailCcError}
                              </div>
                            )}
                          </div>
                        )}
                        {showEmailBcc && (
                          <div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                              }}
                            >
                              <span
                                style={{
                                  padding: "10px 12px",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  minWidth: "48px",
                                  flexShrink: 0,
                                  borderRight: "1px solid #f0f0f0",
                                }}
                              >
                                Bcc
                              </span>
                              <div
                                style={{
                                  flex: 1,
                                  padding: "5px 8px",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                  alignItems: "center",
                                }}
                              >
                                {emailBccList.map((e, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "3px",
                                      padding: "2px 10px",
                                      background: "#f5f3ff",
                                      border: "1px solid #ddd6fe",
                                      borderRadius: "999px",
                                      fontSize: "12px",
                                      color: "#7c3aed",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {e}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setEmailBccList((p) =>
                                          p.filter((_, j) => j !== i),
                                        )
                                      }
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#c4b5fd",
                                        fontSize: "16px",
                                        lineHeight: 1,
                                        padding: "0 0 0 3px",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      &times;
                                    </button>
                                  </span>
                                ))}
                                <input
                                  autoFocus
                                  style={{
                                    flex: 1,
                                    minWidth: "160px",
                                    padding: "4px 2px",
                                    border: "none",
                                    outline: "none",
                                    fontSize: "13px",
                                    background: "transparent",
                                  }}
                                  value={emailBccInput}
                                  onChange={(e) => {
                                    setEmailBccInput(e.target.value);
                                    setEmailBccError("");
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                      e.preventDefault();
                                      handleAddBccEmail();
                                    }
                                  }}
                                  onBlur={handleAddBccEmail}
                                  placeholder={
                                    emailBccList.length === 0
                                      ? "bcc@email.com (press Enter to add)"
                                      : ""
                                  }
                                />
                              </div>
                            </div>
                            {emailBccError && (
                              <div
                                style={{
                                  padding: "2px 12px 5px 60px",
                                  fontSize: "11px",
                                  color: "#ef4444",
                                }}
                              >
                                {emailBccError}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={L}>
                          Email Template{" "}
                          <span
                            style={{
                              color: "#9ca3af",
                              fontWeight: 400,
                              fontSize: "10px",
                              textTransform: "none",
                            }}
                          ></span>
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <select
                            className="fu-sel"
                            value={emailTemplate}
                            onChange={(e) =>
                              handleTemplateChange(e.target.value)
                            }
                            style={{ flex: 1 }}
                          >
                            <option value="">— No Template (Manual) —</option>
                            {templatesLoading && (
                              <option disabled>Loading templates...</option>
                            )}
                            {emailTemplates.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.template_name.replace(/_/g, " ")}
                              </option>
                            ))}
                          </select>
                          {selectedTemplate && (
                            <Tooltip title="Preview Template">
                              <button
                                className="fu-i"
                                onClick={() => setShowPreview(true)}
                                style={{
                                  width: "34px",
                                  height: "34px",
                                  borderRadius: "8px",
                                  border: "1.5px solid #c7d2fe",
                                  background: "#f0f4ff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#667eea",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              >
                                <IC.Eye />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      <div>
                        <label style={L}>Subject</label>
                        <input
                          className={inp}
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          placeholder="Subject..."
                          readOnly={!!selectedTemplate}
                          style={
                            selectedTemplate
                              ? { background: "#f8f9ff", color: "#6b7280" }
                              : {}
                          }
                        />
                      </div>
                      {selectedTemplate ? (
                        <div>
                          <label style={L}>
                            Body{" "}
                            <span
                              style={{
                                color: "#9ca3af",
                                fontWeight: 400,
                                fontSize: "10px",
                                textTransform: "none",
                              }}
                            >
                              (from template)
                            </span>
                          </label>
                          <div
                            style={{
                              padding: "12px",
                              background: "#f8f9ff",
                              borderRadius: "9px",
                              border: "1.5px solid #e8ecff",
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.6,
                              maxHeight: "200px",
                              overflowY: "auto",
                            }}
                            dangerouslySetInnerHTML={{ __html: emailBody }}
                          />
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span>BODY</span>
                            <div style={{ display: "flex", gap: 2, background: "#f1f5f9", borderRadius: 6, padding: 2 }}>
                              {[
                                { mode: "compose", icon: "bx-edit",  label: "Compose" },
                                { mode: "html",    icon: "bx-code",  label: "HTML"    },
                                { mode: "preview", icon: "bx-show",  label: "Preview" },
                              ].map(({ mode, icon, label }) => (
                                <button key={mode} onClick={() => setEmailBodyMode(mode)} style={{
                                  padding: "3px 10px", borderRadius: 4, border: "none",
                                  background: emailBodyMode === mode ? "#fff" : "transparent",
                                  color: emailBodyMode === mode ? "#7c3aed" : "#64748b",
                                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                                  display: "flex", alignItems: "center", gap: 4,
                                  boxShadow: emailBodyMode === mode ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                                }}>
                                  <i className={`bx ${icon}`} style={{ fontSize: 13 }} /> {label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
                            {emailBodyMode === "compose" && (
                              <>
                                <div style={{ display: "flex", alignItems: "center", gap: 2, padding: "6px 10px", borderBottom: "1px solid #f0f0f0", background: "#fafafa", flexWrap: "wrap" }}>
                                  {[
                                    { icon: "bx-bold",      cmd: "bold",      title: "Bold"      },
                                    { icon: "bx-italic",    cmd: "italic",    title: "Italic"    },
                                    { icon: "bx-underline", cmd: "underline", title: "Underline" },
                                  ].map(({ icon, cmd, title }) => (
                                    <button key={cmd} title={title} onMouseDown={(e) => { e.preventDefault(); emailExecCmd(cmd); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>
                                      <i className={`bx ${icon}`} />
                                    </button>
                                  ))}
                                  <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }} />
                                  {[
                                    { icon: "bx-list-ul", cmd: "insertUnorderedList", title: "Bullet List"   },
                                    { icon: "bx-list-ol", cmd: "insertOrderedList",   title: "Numbered List" },
                                  ].map(({ icon, cmd, title }) => (
                                    <button key={cmd} title={title} onMouseDown={(e) => { e.preventDefault(); emailExecCmd(cmd); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                                      <i className={`bx ${icon}`} />
                                    </button>
                                  ))}
                                  <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }} />
                                  {[
                                    { icon: "bx-align-left",   cmd: "justifyLeft",   title: "Align Left"   },
                                    { icon: "bx-align-middle", cmd: "justifyCenter", title: "Align Center" },
                                    { icon: "bx-align-right",  cmd: "justifyRight",  title: "Align Right"  },
                                  ].map(({ icon, cmd, title }) => (
                                    <button key={cmd} title={title} onMouseDown={(e) => { e.preventDefault(); emailExecCmd(cmd); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                                      <i className={`bx ${icon}`} />
                                    </button>
                                  ))}
                                  <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }} />
                                  <button title="Link" onMouseDown={(e) => { e.preventDefault(); const url = window.prompt("Enter URL"); if (url) emailExecCmd("createLink", url); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                                    <i className="bx bx-link" />
                                  </button>
                                  <button title="Undo" onMouseDown={(e) => { e.preventDefault(); emailExecCmd("undo"); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                                    <i className="bx bx-undo" />
                                  </button>
                                  <button title="Redo" onMouseDown={(e) => { e.preventDefault(); emailExecCmd("redo"); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                                    <i className="bx bx-redo" />
                                  </button>
                                </div>
                                <div
                                  ref={emailEditorRef}
                                  contentEditable
                                  suppressContentEditableWarning
                                  onInput={() => setEmailBody(emailEditorRef.current?.innerHTML || "")}
                                  style={{ minHeight: 120, padding: "12px 14px", fontSize: 13, color: "#1a2332", outline: "none", lineHeight: 1.7, wordBreak: "break-word" }}
                                />
                              </>
                            )}
                            {emailBodyMode === "html" && (
                              <textarea
                                value={emailBody}
                                onChange={(e) => setEmailBody(e.target.value)}
                                rows={8}
                                placeholder="Paste or write your HTML here…"
                                style={{ width: "100%", boxSizing: "border-box", padding: "12px", border: "none", outline: "none", resize: "vertical", fontSize: 12, fontFamily: "monospace", color: "#1a2332", lineHeight: 1.6, background: "#fafafa" }}
                              />
                            )}
                            {emailBodyMode === "preview" && (
                              emailBody ? (
                                <div style={{ maxHeight: 280, overflowY: "auto" }}>
                                  <iframe
                                    srcDoc={emailBody}
                                    style={{ width: "100%", border: "none", display: "block", minHeight: 180 }}
                                    onLoad={(e) => { const doc = e.target.contentDocument; if (doc) e.target.style.height = doc.body.scrollHeight + 16 + "px"; }}
                                    title="email-preview"
                                    sandbox="allow-same-origin"
                                  />
                                </div>
                              ) : (
                                <div style={{ minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 13 }}>Nothing to preview</div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                      <div>
                        <label style={L}>Remarks</label>
                        <input
                          className={inp}
                          value={emailRemarks}
                          onChange={(e) => setEmailRemarks(e.target.value)}
                          placeholder="Internal remarks..."
                        />
                      </div>
                      <div>
                        <label style={L}>Follow-up Status</label>
                        <FollowUpStatusSelect
                          value={emailFuStatus}
                          followUpOptionsList={followUpOptionsList}
                          onChange={(val) => {
                            setEmailFuStatus(val);
                          }}
                        />
                      </div>
                      <div className="fu-nfd">
                        <div>
                          <label>
                            <IC.Calendar /> Next Follow-up
                          </label>
                          <input
                            type="date"
                            value={emailNextDate}
                            onChange={(e) => setEmailNextDate(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                        <div>
                          <label>
                            <IC.Clock /> Time
                          </label>
                          <input
                            type="time"
                            value={emailNextTime}
                            onChange={(e) => setEmailNextTime(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                      </div>
                      {templateAttachments.length > 0 && (
                        <div style={{ marginTop: "4px" }}>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                              marginBottom: "5px",
                            }}
                          >
                            Template Attachments
                          </div>
                          <div className="fu-files-row">
                            {templateAttachments.map((f, i) => (
                              <FileChip
                                key={`tpl-${i}`}
                                file={f}
                                onRemove={null}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {emailFiles.length > 0 && (
                        <div style={{ marginTop: "4px" }}>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                              marginBottom: "5px",
                            }}
                          >
                            Added by You
                          </div>
                          <div className="fu-files-row">
                            {emailFiles.map((f, i) => (
                              <FileChip
                                key={i}
                                file={f}
                                onRemove={() => removeEmailFile(i)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "8px",
                          marginTop: "6px",
                        }}
                      >
                        <button
                          className="fu-ab fu-i"
                          onClick={() => emailFileRef.current?.click()}
                          style={{
                            background: "#f0f4ff",
                            color: "#667eea",
                            border: "1.5px solid #c7d2fe",
                            marginRight: "auto",
                          }}
                        >
                          <IC.Attach />
                          Attach Files
                        </button>
                        <button
                          className="fu-ab fu-i"
                          onClick={() => setActivePanel(null)}
                          style={{
                            background: "#f1f5f9",
                            color: "#6b7280",
                            border: "1.5px solid #e2e8f0",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="fu-ab fu-i"
                          disabled={
                            isSaving ||
                            isAllocating ||
                            isTemplateSending ||
                            (!selectedTemplate && !emailBody.replace(/<[^>]*>/g, "").trim())
                          }
                          onClick={handleSendEmail}
                          style={{
                            background: "linear-gradient(135deg,#667eea,#764ba2)",
                            color: "white",
                            boxShadow: "0 4px 12px rgba(102,126,234,0.35)",
                            opacity: (isSaving || isAllocating || isTemplateSending) ? 0.7 : 1,
                            cursor: "pointer",
                          }}
                        >
                          <IC.Send />
                          {isAllocating
                            ? "Assigning..."
                            : isSaving || isTemplateSending
                              ? "Sending..."
                              : "Send"}
                        </button>
                      </div>
                    </div>
                  )}

                  {activePanel === "call" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "11px",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "11px",
                        }}
                      >
                        <div>
                          <label style={L}>Call Outcome</label>
                          <select
                            className="fu-sel"
                            value={callOutcome}
                            onChange={(e) => setCallOutcome(e.target.value)}
                          >
                            {CALL_OUTCOMES.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={L}>Call Duration</label>
                          <input
                            className={inp}
                            value={callDuration}
                            onChange={(e) => setCallDuration(e.target.value)}
                            placeholder="e.g. 15 mins"
                          />
                        </div>
                      </div>
                      <div>
                        <label style={L}>Call Notes</label>
                        <textarea
                          className="fu-ta"
                          placeholder="Notes about this call..."
                          value={callNote}
                          onChange={(e) => setCallNote(e.target.value)}
                          style={{ borderColor: "#fde68a", minHeight: "80px" }}
                        />
                      </div>
                      <div>
                        <label style={L}>Follow-up Status</label>
                        <FollowUpStatusSelect
                          value={callFuStatus}
                          followUpOptionsList={followUpOptionsList}
                          onChange={(val) => {
                            setCallFuStatus(val);
                          }}
                        />
                      </div>
                      <div className="fu-nfd">
                        <div>
                          <label>
                            <IC.Calendar /> Next Follow-up
                          </label>
                          <input
                            type="date"
                            value={callNextDate}
                            onChange={(e) => setCallNextDate(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                        <div>
                          <label>
                            <IC.Clock /> Time
                          </label>
                          <input
                            type="time"
                            value={callNextTime}
                            onChange={(e) => setCallNextTime(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: "8px",
                          marginTop: "6px",
                        }}
                      >
                        <button
                          className="fu-ab fu-i"
                          onClick={() => setActivePanel(null)}
                          style={{
                            background: "#f1f5f9",
                            color: "#6b7280",
                            border: "1.5px solid #e2e8f0",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="fu-ab fu-i"
                          disabled={isSaving || isAllocating}
                          onClick={handleLogCall}
                          style={{
                            background:
                              "linear-gradient(135deg,#f59e0b,#d97706)",
                            color: "white",
                            boxShadow: "0 4px 12px rgba(245,158,11,0.3)",
                            opacity: isSaving || isAllocating ? 0.7 : 1,
                          }}
                        >
                          <IC.Check />
                          {isAllocating
                            ? "Assigning..."
                            : isSaving
                              ? "Logging..."
                              : "Log Call"}
                        </button>
                      </div>
                    </div>
                  )}

                  {activePanel === "note" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "11px",
                      }}
                    >
                      <div>
                        <label style={L}>Internal Note</label>
                        <textarea
                          className="fu-ta"
                          placeholder="Write your note... (visible only to team)"
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          style={{ borderColor: "#ddd6fe", minHeight: "100px" }}
                        />
                      </div>
                      <div className="fu-nfd">
                        <div>
                          <label>
                            <IC.Calendar /> Next Follow-up
                          </label>
                          <input
                            type="date"
                            value={noteNextDate}
                            onChange={(e) => setNoteNextDate(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                        <div>
                          <label>
                            <IC.Clock /> Time
                          </label>
                          <input
                            type="time"
                            value={noteNextTime}
                            onChange={(e) => setNoteNextTime(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: "8px",
                          marginTop: "6px",
                        }}
                      >
                        <button
                          className="fu-ab fu-i"
                          onClick={() => setActivePanel(null)}
                          style={{
                            background: "#f1f5f9",
                            color: "#6b7280",
                            border: "1.5px solid #e2e8f0",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="fu-ab fu-i"
                          disabled={isSaving || isAllocating}
                          onClick={handleSaveNote}
                          style={{
                            background:
                              "linear-gradient(135deg,#8b5cf6,#7c3aed)",
                            color: "white",
                            boxShadow: "0 4px 12px rgba(139,92,246,0.3)",
                            opacity: isSaving || isAllocating ? 0.7 : 1,
                          }}
                        >
                          <IC.Note />
                          {isAllocating
                            ? "Assigning..."
                            : isSaving
                              ? "Saving..."
                              : "Save Note"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── TIMELINE ── */}
          <div
            style={{ flex: 1, overflowY: "auto", padding: "14px 22px 32px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <IC.Filter /> Filter:
              </span>
              {["All", "Call", "Note"].map((f) => {
                const count =
                  f === "All"
                    // emailThreadItems is already de-duped against email follow-ups
                    ? allFollowUps.filter((fu) => !isThreadedEmail(fu)).length +
                      emailThreadItems.length
                    : allFollowUps.filter((fu) => {
                        const type = fu.followup_type?.toLowerCase();
                        if (f === "Call") return type === "call";
                        if (f === "Note")
                          return type === "other" || type === "note";
                        return false;
                      }).length;
                return (
                  <button
                    key={f}
                    className="fu-pill fu-i"
                    onClick={() => setActiveFilter(f)}
                    style={{
                      background:
                        activeFilter === f
                          ? "linear-gradient(135deg,#667eea,#764ba2)"
                          : "white",
                      color: activeFilter === f ? "white" : "#6b7280",
                      borderColor: activeFilter === f ? "#667eea" : "#e2e8f0",
                    }}
                  >
                    {f}
                    <span style={{ opacity: 0.7 }}> ({count})</span>
                  </button>
                );
              })}
              <button
                className="fu-pill fu-i"
                // Everything is rendered inline, so just switch tab and refresh
                onClick={() => { setActiveFilter("Conversations"); loadAllConversations(); }}
                style={{
                  background: activeFilter === "Conversations"
                    ? "linear-gradient(135deg,#0ea5e9,#0284c7)"
                    : "white",
                  color: activeFilter === "Conversations" ? "white" : "#6b7280",
                  borderColor: activeFilter === "Conversations" ? "#0ea5e9" : "#e2e8f0",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <IC.Mail />
                Inbox
              </button>
            </div>

            {/* ── EMAIL CONVERSATIONS VIEW ── */}
            {activeFilter === "Conversations" && (
              <div style={{ paddingTop: "4px" }}>
                {convLoading && (
                  <div style={{ textAlign: "center", padding: "30px", color: "#9ca3af" }}>Loading conversations...</div>
                )}
                {!convLoading && inboxGroups.length === 0 && (
                  <div style={{ textAlign: "center", padding: "50px 20px", color: "#9ca3af" }}>
                    <div style={{ fontSize: "40px", marginBottom: "10px" }}>✉️</div>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#6b7280" }}>No conversations yet</div>
                    <div style={{ fontSize: "12px", marginTop: "4px" }}>Send an email using the Compose Mail button above</div>
                  </div>
                )}
                {/* Every conversation rendered inline — no click-through thread view */}
                {!convLoading && inboxGroups.map((group) => (
                  <div key={group.conversation_id} style={{ marginBottom: "22px" }}>
                    {/* Conversation heading */}
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", borderRadius: "10px", background: "#f8fafc", border: "1.5px solid #e2e8f0", marginBottom: "12px" }}>
                      <span style={{ fontSize: "13px", fontWeight: 700, color: "#1f2937", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {group.subject}
                      </span>
                      <span style={{ padding: "2px 8px", borderRadius: "20px", fontSize: "10px", fontWeight: 600, background: "#eef2ff", color: "#4f46e5", flexShrink: 0 }}>
                        {group.messages.length} {group.messages.length === 1 ? "message" : "messages"}
                      </span>
                      {group.lastAt && (
                        <span style={{ fontSize: "11px", color: "#9ca3af", marginLeft: "auto", flexShrink: 0 }}>
                          {moment(group.lastAt).format("DD MMM YYYY, h:mm A")}
                        </span>
                      )}
                    </div>

                    {group.messages.map((msg, idx) => {
                      const isOut = msg.direction === "outbound";
                      const isFailed = msg.status === "failed";
                      const attachments = (() => { try { return JSON.parse(msg.attachments || "null") || []; } catch { return []; } })();
                      return (
                        <div key={msg.id || idx} style={{ marginBottom: "14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", flexDirection: isOut ? "row-reverse" : "row", flexWrap: "wrap" }}>
                            <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: isOut ? "linear-gradient(135deg,#667eea,#764ba2)" : "linear-gradient(135deg,#06b6d4,#0284c7)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "10px", fontWeight: 700, flexShrink: 0 }}>
                              {isOut ? "CMS" : initials(lead.name)}
                            </div>
                            <div style={{ fontSize: "11px", color: "#6b7280", textAlign: isOut ? "right" : "left", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 600, color: "#374151" }}>{isOut ? "You" : lead.name}</span>
                              {" · "}<span>{msg.from_email}</span>
                              {(msg.sent_at || msg.created_at) && <span style={{ marginLeft: "6px" }}>{moment(msg.sent_at || msg.created_at).format("DD MMM YYYY, h:mm A")}</span>}
                              {isFailed && (
                                <span title={msg.error || "Delivery failed"}
                                  style={{ background: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 12, fontSize: 11, fontWeight: 700, padding: "1px 8px", cursor: "default" }}>
                                  ⚠ Failed
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ marginLeft: isOut ? "0" : "38px", marginRight: isOut ? "38px" : "0" }}>
                            <div style={{ borderRadius: "12px", border: "1.5px solid", borderColor: isFailed ? "#fecaca" : isOut ? "#c7d2fe" : "#d1fae5", background: isFailed ? "#fef2f2" : isOut ? "#f0f4ff" : "#f0fdf4", overflow: "hidden", borderLeft: isFailed ? "3px solid #dc2626" : undefined }}>
                              <iframe
                                srcDoc={msg.body || ""}
                                style={{ width: "100%", border: "none", display: "block", minHeight: "60px" }}
                                onLoad={(e) => { const doc = e.target.contentDocument; if (doc) e.target.style.height = (doc.body?.scrollHeight || 60) + 16 + "px"; }}
                                title={`msg-${msg.id || idx}`}
                                sandbox="allow-same-origin"
                              />
                            </div>
                            {attachments.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
                                {attachments.map((att, ai) => (
                                  <a key={ai} href={att.url} target="_blank" rel="noreferrer"
                                    style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 10px", borderRadius: "7px", border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#374151", fontSize: "11px", fontWeight: 600, textDecoration: "none" }}>
                                    📎 {att.name}
                                  </a>
                                ))}
                              </div>
                            )}
                            {isFailed && msg.error && (
                              <div style={{ marginTop: "8px", padding: "6px 10px", background: "#fee2e2", borderRadius: "6px", fontSize: "12px", color: "#b91c1c", display: "flex", alignItems: "center", gap: "6px" }}>
                                ⚠ Delivery failed: {msg.error}
                              </div>
                            )}
                            {!isOut && (
                              <button
                                onClick={() => {
                                  setReplyConvId(group.conversation_id);
                                  setEmailSubject(msg.subject ? (msg.subject.startsWith("Re:") ? msg.subject : `Re: ${msg.subject}`) : "");
                                  setActivePanel("email");
                                }}
                                style={{ marginTop: "6px", display: "inline-flex", alignItems: "center", gap: "5px", padding: "5px 12px", borderRadius: "7px", border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#0078d4", fontSize: "11px", fontWeight: 600, cursor: "pointer" }}
                              >
                                ↩ Reply
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {group.messages.length === 0 && (
                      <div style={{ textAlign: "center", padding: "20px", color: "#9ca3af", fontSize: "13px" }}>No messages in this conversation</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeFilter !== "Conversations" && fuLoading && (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#9ca3af",
                }}
              >
                Loading timeline...
              </div>
            )}

            {activeFilter !== "Conversations" && !fuLoading &&
              timelineItems.map((fu, idx) => {
                // ── Email from the inbox thread (inbound reply or outbound mail) ──
                if (fu._isInboundEmail) {
                  const isInboundMail = fu.direction !== "outbound";
                  const mailTheme = isInboundMail
                    ? { bg: "#f0fdf4", border: "#d1fae5", accent: "#059669", avatar: "linear-gradient(135deg,#06b6d4,#0284c7)" }
                    : { bg: "#eff6ff", border: "#dbeafe", accent: "#2563eb", avatar: "linear-gradient(135deg,#3b82f6,#1d4ed8)" };
                  const attachments = (() => {
                    let atts = fu.attachments;
                    if (!atts) return [];
                    if (typeof atts === "string") { try { atts = JSON.parse(atts); } catch { return []; } }
                    return Array.isArray(atts) ? atts : [];
                  })();
                  return (
                    <div key={`inbox_${fu.id}`} className="fu-anim" style={{ display: "flex", gap: "12px", marginBottom: "4px" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                        <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: mailTheme.avatar, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "11px", fontWeight: 700, border: "3px solid white", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", zIndex: 1 }}>
                          {isInboundMail ? initials(lead.name) : "You"}
                        </div>
                        {idx < timelineItems.length - 1 && (
                          <div style={{ width: "2px", flex: 1, background: "linear-gradient(to bottom,#e8ecff,transparent)", margin: "4px 0", minHeight: "18px" }} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, paddingBottom: "12px" }}>
                        <div style={{ background: mailTheme.bg, border: `1.5px solid ${mailTheme.border}`, borderRadius: "14px", padding: "14px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                            <span style={{ padding: "2px 8px", borderRadius: "20px", fontSize: "10px", fontWeight: 700, background: "white", color: mailTheme.accent, border: `1px solid ${mailTheme.border}` }}>
                              {isInboundMail ? "↙ Received" : "↗ Sent"}
                            </span>
                            <span style={{ fontSize: "11px", color: "#6b7280" }}>
                              {isInboundMail ? fu.from_email : `To: ${fu.to_email || ""}`}
                            </span>
                            {fu.status === "failed" && (
                              <span style={{ fontSize: "10px", fontWeight: 700, color: "#b91c1c", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 20, padding: "2px 8px" }}>
                                ⚠ Failed
                              </span>
                            )}
                            {(fu.sent_at || fu.created_at) && (
                              <span style={{ fontSize: "11px", color: "#9ca3af", marginLeft: "auto" }}>{moment(fu.sent_at || fu.created_at).format("DD MMM YYYY, h:mm A")}</span>
                            )}
                          </div>
                          {fu.subject && <div style={{ fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "6px" }}>{fu.subject}</div>}
                          <iframe
                            srcDoc={fu.body || ""}
                            style={{ width: "100%", border: "none", display: "block", minHeight: "40px", borderRadius: "6px" }}
                            onLoad={(e) => { const doc = e.target.contentDocument; if (doc) e.target.style.height = (doc.body?.scrollHeight || 40) + 12 + "px"; }}
                            title={`inbox_mail_${fu.id}`}
                            sandbox="allow-same-origin"
                          />
                          {attachments.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                              {attachments.map((att, i) => {
                                const name = att.name || att.filename || `Attachment ${i + 1}`;
                                const url = att.url || att.path || null;
                                return (
                                  <a key={i} href={url || "#"} target={url ? "_blank" : undefined} rel="noopener noreferrer"
                                    style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 7, background: "white", border: `1px solid ${mailTheme.border}`, color: "#374151", fontSize: 11, fontWeight: 500, textDecoration: "none" }}>
                                    📎 {name}
                                  </a>
                                );
                              })}
                            </div>
                          )}
                          <button
                            onClick={() => { setActiveFilter("Conversations"); setActiveConvId(fu.conversation_id); getEmailThread(lead.id, fu.conversation_id).then(setThreadMessagesSorted).catch(() => {}); }}
                            style={{ marginTop: "8px", display: "inline-flex", alignItems: "center", gap: "5px", padding: "5px 12px", borderRadius: "7px", border: `1.5px solid ${mailTheme.border}`, background: "white", color: mailTheme.accent, fontSize: "11px", fontWeight: 600, cursor: "pointer" }}
                          >
                            {isInboundMail ? "↩ Reply" : "View Thread"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }

                const cfg = getTypeCfg(fu.followup_type);
                const isIn = fu.direction === "inbound";
                return (
                  <div
                    key={fu.id}
                    className="fu-anim"
                    style={{
                      display: "flex",
                      gap: "12px",
                      marginBottom: "4px",
                      flexDirection: isIn ? "row-reverse" : "row",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          background: isIn
                            ? "linear-gradient(135deg,#06b6d4,#0284c7)"
                            : "linear-gradient(135deg,#667eea,#764ba2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "11px",
                          fontWeight: 700,
                          border: "3px solid white",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          zIndex: 1,
                        }}
                      >
                        {isIn
                          ? initials(lead.name)
                          : isMyEntry(fu)
                            ? "You"
                            : fu.created_by_name
                              ? initials(fu.created_by_name)
                              : "You"}
                      </div>
                      {idx < timelineItems.length - 1 && (
                        <div
                          style={{
                            width: "2px",
                            flex: 1,
                            background:
                              "linear-gradient(to bottom,#e8ecff,transparent)",
                            margin: "4px 0",
                            minHeight: "18px",
                          }}
                        />
                      )}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        maxWidth: "calc(100% - 56px)",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "7px",
                          marginBottom: "6px",
                          flexWrap: "wrap",
                          flexDirection: isIn ? "row-reverse" : "row",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "3px 9px",
                            borderRadius: "20px",
                            background: cfg.bg,
                            color: cfg.color,
                            fontSize: "11px",
                            fontWeight: 700,
                            border: `1.5px solid ${cfg.border}`,
                          }}
                        >
                          <cfg.Icon />
                          {cfg.label}
                          {fu.whatsapp_msg_type &&
                            fu.whatsapp_msg_type !== "text" && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  marginLeft: "2px",
                                }}
                              >
                                {getMediaIcon(fu.whatsapp_msg_type)}
                              </span>
                            )}
                        </span>
                        <span style={{ fontSize: "11px", color: "#6b7280" }}>
                          {formatDateTime(fu.followup_date)}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            color: isIn ? "#0284c7" : "#667eea",
                            fontWeight: 700,
                          }}
                        >
                          {isIn ? lead.name : isMyEntry(fu) ? "You" : fu.created_by_name || "Admin"}
                        </span>
                      </div>
                      <div
                        className="fu-card"
                        style={{
                          background: isIn ? "#f0f9ff" : cfg.bg,
                          borderColor: isIn ? "#bae6fd" : cfg.border,
                        }}
                      >
                        {fu.subject && (
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: "13px",
                              color: "#1f2937",
                              marginBottom: "7px",
                              paddingBottom: "7px",
                              borderBottom: `1px solid ${cfg.border}`,
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <IC.Mail />
                            {fu.subject}
                            {/* A Teams invite is an email, but not one the rep
                                wrote — this keeps it apart from real outreach. */}
                            {isDemoFollowUp(fu) && (
                              <span style={{ marginLeft: "auto", background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: 20, padding: "1px 9px", fontSize: "10px", fontWeight: 800, letterSpacing: ".04em" }}>
                                DEMO
                              </span>
                            )}
                          </div>
                        )}
                        {fu.followup_type === "call" && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "3px 10px",
                                borderRadius: "20px",
                                background:
                                  fu.call_outcome === "Connected" ||
                                  fu.status === "completed"
                                    ? "#f0fdf4"
                                    : "#fef2f2",
                                color:
                                  fu.call_outcome === "Connected" ||
                                  fu.status === "completed"
                                    ? "#16a34a"
                                    : "#dc2626",
                                fontSize: "11px",
                                fontWeight: 700,
                                border: `1.5px solid ${fu.call_outcome === "Connected" || fu.status === "completed" ? "#bbf7d0" : "#fecaca"}`,
                              }}
                            >
                              <IC.Audio />
                              {fu.call_outcome || fu.status}
                              {fu.call_duration && ` · ${fu.call_duration}`}
                            </span>
                            {fu.status === "completed" && <AudioWave />}
                          </div>
                        )}
                        <FollowUpBody
                          text={fu.content}
                          style={{
                            margin: 0,
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.65,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            // A sent mail can carry a wide logo or table.
                            maxWidth: "100%",
                            overflowX: "auto",
                          }}
                        />
                        {/* Only for a plain-text body: an HTML invite already
                            carries its own "Join Microsoft Teams Meeting" link,
                            and a second button below it is just noise. */}
                        {!looksLikeHtml(fu.content) && teamsLinkIn(fu.content) && (
                          <a
                            href={teamsLinkIn(fu.content)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, padding: "5px 14px", borderRadius: 20, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", fontSize: 12, fontWeight: 700, textDecoration: "none" }}
                          >
                            Join Teams Meeting
                          </a>
                        )}
                        {parseAttachments(fu.attachments).map((att, i) => {
                          const fType = getFileType(att.url || att.name);
                          return (
                            <div
                              key={i}
                              style={{
                                marginTop: "9px",
                                borderRadius: "10px",
                                overflow: "hidden",
                                border: `1.5px solid ${cfg.border}`,
                                background: "white",
                              }}
                            >
                              {fType === "image" && att.url && (
                                <a
                                  href={att.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: "block",
                                    cursor: "pointer",
                                  }}
                                >
                                  <img
                                    src={att.url}
                                    alt={att.name}
                                    style={{
                                      width: "100%",
                                      maxHeight: "220px",
                                      objectFit: "cover",
                                      display: "block",
                                      borderBottom: `1px solid ${cfg.border}`,
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                </a>
                              )}
                              {fType === "video" && att.url && (
                                <video
                                  controls
                                  preload="metadata"
                                  style={{
                                    width: "100%",
                                    maxHeight: "220px",
                                    display: "block",
                                    borderBottom: `1px solid ${cfg.border}`,
                                    background: "#000",
                                  }}
                                >
                                  <source src={att.url} />
                                  Your browser does not support video.
                                </video>
                              )}
                              {fType === "audio" && att.url && (
                                <div
                                  style={{
                                    padding: "10px 12px",
                                    borderBottom: `1px solid ${cfg.border}`,
                                    background: "#fafafa",
                                  }}
                                >
                                  <audio
                                    controls
                                    preload="metadata"
                                    style={{ width: "100%", height: "36px" }}
                                  >
                                    <source src={att.url} />
                                  </audio>
                                </div>
                              )}
                              {fType === "pdf" && att.url && (
                                <a
                                  href={att.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: "block",
                                    padding: "14px 12px",
                                    borderBottom: `1px solid ${cfg.border}`,
                                    background: "#fef2f2",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "40px",
                                        height: "48px",
                                        background: "#dc2626",
                                        borderRadius: "6px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "white",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        flexShrink: 0,
                                      }}
                                    >
                                      PDF
                                    </div>
                                    <div>
                                      <p
                                        style={{
                                          margin: 0,
                                          fontSize: "12px",
                                          fontWeight: 600,
                                          color: "#1f2937",
                                        }}
                                      >
                                        Click to view PDF
                                      </p>
                                      <p
                                        style={{
                                          margin: "2px 0 0",
                                          fontSize: "11px",
                                          color: "#9ca3af",
                                        }}
                                      >
                                        {att.name}
                                      </p>
                                    </div>
                                  </div>
                                </a>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "9px",
                                  padding: "8px 12px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "7px",
                                    background: cfg.bg,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: cfg.color,
                                    flexShrink: 0,
                                  }}
                                >
                                  {fType === "image" ? (
                                    <IC.Image />
                                  ) : (
                                    <FileTypeIcon type={fType} />
                                  )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: "#1f2937",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {att.name}
                                  </p>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: "10px",
                                      color: "#9ca3af",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {fType}
                                    {att.size ? ` · ${att.size}` : ""}
                                  </p>
                                </div>
                                {att.url && (
                                  <button
                                    onClick={() =>
                                      forceDownload(att.url, att.name)
                                    }
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      padding: "5px 12px",
                                      background: cfg.bg,
                                      color: cfg.color,
                                      border: `1.5px solid ${cfg.border}`,
                                      borderRadius: "7px",
                                      fontSize: "11px",
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      transition: "all 0.15s",
                                      cursor: "pointer",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background =
                                        cfg.color;
                                      e.currentTarget.style.color = "white";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = cfg.bg;
                                      e.currentTarget.style.color = cfg.color;
                                    }}
                                  >
                                    ↓ Download
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {/* Who the mail actually went to — worth stating for an
                            invite nobody on the team typed out. */}
                        {fu.followup_type === "email" && fu.recipient && (
                          <div style={{ marginTop: 8, fontSize: 11, color: "#9ca3af", wordBreak: "break-all" }}>
                            Sent to <span style={{ color: "#6b7280", fontWeight: 600 }}>{fu.recipient}</span>
                          </div>
                        )}
                        {fu.next_followup_date && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "6px 10px",
                              background: "#fffbeb",
                              borderRadius: "8px",
                              border: "1px solid #fde68a",
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              fontSize: "11px",
                              color: "#d97706",
                              fontWeight: 600,
                            }}
                          >
                            <IC.Clock /> Next: {formatNextFollowUp(fu)}
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "7px",
                          }}
                        >
                          {/* Same gate as the scheduled card above — deleting a
                              timeline entry edits the lead's history. */}
                          <div style={{ ...lockedStyle, display: "flex", gap: "4px" }}>
                            <Tooltip title="Delete">
                              <button
                                className="fu-i"
                                onClick={() => handleDeleteFU(fu)}
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "6px",
                                  border: "1.5px solid #fecaca",
                                  background: "#fef2f2",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#dc2626",
                                  cursor: "pointer",
                                }}
                              >
                                <IC.Trash />
                              </button>
                            </Tooltip>
                          </div>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px",
                              fontSize: "10px",
                              color:
                                fu.delivery_status === "read"
                                  ? "#16a34a"
                                  : "#9ca3af",
                              fontWeight: 500,
                            }}
                          >
                            {fu.delivery_status === "read" ? (
                              <>
                                <IC.DblCheck />
                                &nbsp;Read
                              </>
                            ) : fu.delivery_status === "delivered" ? (
                              <>
                                <IC.Check />
                                &nbsp;Delivered
                              </>
                            ) : fu.delivery_status === "sent" ? (
                              <>
                                <IC.Check />
                                &nbsp;Sent
                              </>
                            ) : fu.status === "completed" ? (
                              "✓ Done"
                            ) : (
                              fu.status
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            {activeFilter !== "Conversations" && !fuLoading && timelineItems.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "50px 20px",
                  color: "#9ca3af",
                }}
              >
                <div style={{ fontSize: "44px", marginBottom: "10px" }}>💬</div>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#6b7280",
                  }}
                >
                  No follow-ups yet
                </div>
                <div style={{ fontSize: "12px", marginTop: "4px" }}>
                  Use the compose buttons above to create your first follow-up
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnquiryFollowUp;